/*
 * flower-vase-renderer.js - Family Vase poster renderer.
 *
 * Forked from flower-poster-renderer.js on 2026-09-19 (bouquet commit bf91de7).
 * Starts as a byte-for-byte clone apart from the global name, so the Family Vase
 * product renders identically to the bouquet on day one and every later change is
 * a visible, reviewable diff against a known-good baseline.
 *
 * Where this is HEADED (and why it cannot stay a bouquet background):
 *   - flowers rise from a vase mouth, not a hand-tie: TIE_POINT_FRACTION and the
 *     stem-cutoff band are replaced by a vase anchor + fan-out, and the vase
 *     OCCLUDES the stems (new z-order between stems and vessel)
 *   - the title sits ABOVE the art, not in the footer band
 *   - captions are name-over-flower PAIRS in a separated grid, not two lines
 *   - the background is two zones (wall + table) meeting at a horizon, with a
 *     cast shadow, rather than one image behind everything
 */
/* assets/flower-vase-renderer.js
   Flower Bouquet Poster renderer

   INPUT:
     {
       sizeKey: "50x70",
       rectColor: "#EDEBE7",
       flowers: [
         { key: "<flowerKey>", name: "Anna", role: "parent"|"child", slotIndex, cmFactor, direction, shapeRole },
         ...
       ]
     }

   Slots: 2 parents (or 1, see below) + up to 12 children = max 14 flowers. Customers cap
   out at 12 (2 + 10); counts 13/14 exist for DEV calibration only (Bouquet Admin).

   LAYOUT: hand-tuned anchor templates per flower count (2..14), not a computed formula --
   real bouquets are asymmetric, hand-arranged compositions that a generic layout formula
   can't reproduce convincingly. Each template is a fixed list of anchors (x/y as fractions
   of the artboard, scale, rotation, z-order, facing) authored/tuned against real reference
   photos where available (see flower-bouquet-render-plan.html for the validation work).
   Layout resolution is layered (see the "THREE ADJUSTMENT LAYERS" comment in section 5):
   count baseline -> split-specific layout ("p2c3") -> per-species offset at a position
   ("rose" at "p2c3:child2"). Assignment is positional: parent i -> slot parent<i>,
   child j -> slot child<j>, in the customer's drag-drop order.

   Design space is 900 wide; height is derived per print size so nothing stretches
   (matches assets/zodiac-poster-renderer.js's approach) -- the old fixed 900x1200 space
   distorted non-uniformly across the 30x40 / 40x50 / 50x70 sizes.

   FEATURES:
   - Auto-bouquet layout for 2..14 flowers via hand-tuned templates (13/14 dev-only)
   - Names rendered in footer area
   - Export JPG, PNG, PDF 300 DPI
*/

(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.FlowerVaseRenderer = factory();
})(typeof self !== "undefined" ? self : this, function () {
  // ============================================================
  // 1) CONSTANTS
  // ============================================================
  const VERSION = "v2026-10-25.2";
  const DESIGN_W = 900;

  // Baseline multiplier applied to every flower's size (merchant-tuned). The section's
  // "Standard scale" dev slider and pasted overrides adjust from this base.
  const DEFAULT_GLOBAL_SCALE = 1.6;

  const SIZE_MM = {
    "30x40": { w: 300, h: 400 },
    "40x50": { w: 400, h: 500 },
    "50x70": { w: 500, h: 700 },
  };

  function designHeightFor(sizeKey) {
    var mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    return Math.round(DESIGN_W * (mm.h / mm.w));
  }

  // ============================================================
  // 2) DEFAULT STYLE
  // ============================================================
  const DEFAULT_STYLE = {
    rectColor: "#EDEBE7",
    posterBgColor: "#FFFFFF",
    textColor: "#111111",
    // Names line font. "GothamPoster" = Gotham Book (@font-face in flower-poster.liquid,
    // Shopify Files CDN Gotham_Book.otf); RockwellPoster stays as the draw-time fallback.
    fontFamily: "GothamPoster",

    // RECT (visible colored panel) margins — matched to the reference "rättfärg" print
    // (top 5.86%, sides 7.8%, bottom band 13.48%). These size ONLY the visible rect / bg /
    // footer. The bouquet is laid out in a SEPARATE frozen frame (BOUQUET_FRAME_MARGINS
    // below), so changing these does NOT move the bouquet.
    // Room ABOVE the art for the title + ingress (the bouquet had 0.0586 and put its
    // headline in the footer instead).
    marginTopPct: 0.138,
    marginSidePct: 0.078,
    footerHeightPct: 0.127,  // + footerGapPct(0.018): the caption grid needs two rows
    footerGapPct: 0.018,
    footerPaddingXPct: 0.078,
    footerPaddingBottomPct: 0.025,

    // Text styling — designer spec 2026-07-12, given for the 30x40 reference:
    // headline 14pt, names/flowers 11pt, radavstånd (leading) 24pt, tracking 500
    // (= 500/1000 em). pt -> design px at 900px / 30cm width: 1pt = 1.0583px.
    // All fractions of DESIGN_W so every poster size keeps the same proportions.
    headlineSizePct: 0.016463,  // 14pt
    nameSizePct: 0.012935,      // 11pt (names + flowers lines, the one-row FLOOR)
    footerLineGapPct: 0.028222, // radavstånd 24pt: the DEFAULT gap BETWEEN blocks
                                // (header -> names -> flowers)
    footerTightLineGapPct: 0.0185, // ~16pt: gaps in the fully crowded case (BOTH names
                                   // and flowers wrap -> 4 rows) (merchant 2026-07-18)
    footerMediumLineGapPct: 0.023518, // 20pt: gaps in the 3-row case (exactly ONE of
                                      // names/flowers wraps) (merchant 2026-07-18)
    letterSpacingEm: 0.5,       // tracking 500 -> 0.5em, scales with the font px

    // Vase title block + caption grid. Sizes are fractions of DESIGN_W, positions
    // fractions of the design HEIGHT, so every print size keeps these proportions.
    titleSizePct: 0.0315,
    titleYPct: 0.072,
    ingressSizePct: 0.0145,
    ingressYPct: 0.104,
    captionNameSizePct: 0.0135,
    captionFlowerRatio: 0.72,   // the flower line is smaller than the name above it
    captionLineGapRatio: 1.6,   // row -> row within a block
    captionBlockGapRatio: 2.0,  // the names block -> the flowers block
    captionMaxPerRow: 6,
    captionTopGapPct: 0.015,
    // Panel shape (height / width) used only until a background is uploaded; with one,
    // the artwork's own aspect wins.
    panelAspect: 1.1655,
    // Extra air between the rect's bottom edge and the headline, ON TOP of the
    // footerGapPct band gap (merchant 2026-07-16: "header text closer to the rect" —
    // halved from the 0.016 that 2026-07-12 asked for).
    footerTextTopGapPct: 0.005, // of design HEIGHT (~6px at 50x70) -- header nudged up (merchant 2026-07-17)

    // Paper texture overlay (same as the single-flower poster): blend mode +
    // default opacity; the section passes textureOpacity from its setting.
    textureComposite: "multiply",
    textureOpacity: 0.4,

    flowerShadow: true,
    debug: false,
  };

  // FROZEN bouquet layout frame — the margins the bouquet was hand-calibrated against.
  // renderPoster positions/clips the bouquet in THIS frame, independent of the (now larger)
  // visible-rect margins in DEFAULT_STYLE and of the dev margin sliders. This is what keeps
  // the bouquet fixed while the rect was resized to match the reference print.
  const BOUQUET_FRAME_MARGINS = {
    marginTopPct: 0.055,
    marginSidePct: 0.073,
    footerHeightPct: 0.095,
    footerGapPct: 0.018,
  };

  // Per-SIZE extra footer reserve (fraction of design HEIGHT added to footerHeightPct).
  // 40x50 is the squarest ratio -> shortest design height -> smallest ABSOLUTE footer
  // band, yet the names font is a constant px. Without this, a 4-row footer (headline +
  // wrapped names + wrapped flowers) crowds the bottom edge on 40x50 only. The bump makes
  // the visible rect a touch shorter there, freeing ~13px of white for the text. It stays
  // small on purpose: the flower layer is clipped to the rect, so a big bump would trim
  // the bouquet -- this only shortens the solid-colour band that sits BELOW the greenery
  // (already covered by the stem-cutoff band), not the flowers themselves. Other sizes: 0.
  var SIZE_FOOTER_BUMP = { "40x50": 0.012 };

  // Where every stem visually converges, as a fraction of the artboard. One fixed point,
  // tuned once against the reference posters -- not per flower.
  const TIE_POINT_FRACTION = { x: 0.5, y: 0.86 };

  // Base flower box size in design px (before per-slot `scale`), tuned to roughly fill the
  // gaps between anchors in the tightened templates -- adjust here if art comes in bigger/smaller.
  const FLOWER_BASE_SIZE = 260;

  // ============================================================
  // 3) GEOMETRY + CANVAS HELPERS
  // ============================================================
  function getArtboardRect(style, designH) {
    const x = style.marginSidePct * DESIGN_W;
    const y = style.marginTopPct * designH;
    const w = DESIGN_W - 2 * x;
    const footerGap = style.footerGapPct * designH;
    const footerHeight = style.footerHeightPct * designH;
    const h = designH - y - footerGap - footerHeight;
    return { x: x, y: y, w: w, h: Math.max(1, h) };
  }

  function getFooterRect(style, artboard, designH) {
    const footerGap = style.footerGapPct * designH;
    const x = 0;
    const y = artboard.y + artboard.h + footerGap;
    const w = DESIGN_W;
    const h = Math.max(1, designH - y);
    return { x, y, w, h };
  }

  function mmToPx(mm, dpi) {
    return Math.round((mm / 25.4) * dpi);
  }

  // input.bleedDesignPx: how far past the trim edge the canvas extends, in DESIGN
  // units. A number means the same on both axes; {x,y} covers a non-uniform
  // scale. Absent / 0 = a trim-sized canvas, i.e. every on-screen render.
  function bleedDesign(input) {
    var b = input && input.bleedDesignPx;
    if (typeof b === "number" && b > 0) return { x: b, y: b };
    if (b && typeof b.x === "number") return { x: b.x || 0, y: b.y || 0 };
    return { x: 0, y: 0 };
  }

  // pxW/pxH = the TRIM size in device pixels. `bleedPx` (optional) grows the
  // buffer by that many pixels on every side and shifts the origin outwards, so
  // the poster is DRAWN onto the full sheet: the paper colour and the texture
  // overlay run past the trim line as real artwork instead of being faked
  // afterwards (a flat rect, or the edge row stretched -- both of which showed).
  // Callers must pass the same bleed on to renderPoster via input.bleedDesignPx.
  function makeOffscreenCanvas(pxW, pxH, bleedPx) {
    bleedPx = Math.round(bleedPx || 0);
    const off = document.createElement("canvas");
    off.width = pxW + bleedPx * 2;
    off.height = pxH + bleedPx * 2;
    const offCtx = off.getContext("2d");
    offCtx.imageSmoothingEnabled = true;
    offCtx.imageSmoothingQuality = "high";
    const s = pxW / DESIGN_W;
    offCtx.setTransform(s, 0, 0, s, bleedPx, bleedPx);
    return { off, offCtx, scale: s, bleedPx: bleedPx, bleedDesignPx: bleedPx / s };
  }

  function setupPreviewCanvas(canvas, ctx, dprMax, sizeKey) {
    dprMax = dprMax || 3;
    const dpr = Math.max(1, Math.min(dprMax, window.devicePixelRatio || 1));
    const rect = canvas.getBoundingClientRect();
    // Keep the FRACTIONAL width: flooring the CSS height to a whole pixel left a
    // sub-pixel sliver of the wrap's white background visible below the canvas --
    // invisible while the poster margin was pure white, but a visible "white line"
    // once the paper texture toned the poster (merchant 2026-07-16).
    const cssW = Math.max(1, rect.width);
    const mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    const ratio = (Number(mm.h) || 700) / (Number(mm.w) || 500);
    const cssH = cssW * ratio;
    canvas.style.height = cssH + "px";
    canvas.width = Math.max(1, Math.round(cssW * dpr));
    canvas.height = Math.max(1, Math.round(cssH * dpr));
    const scale = canvas.width / DESIGN_W;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
  }

  function setupPreviewCanvasForSize(canvas, ctx, sizeKey, dprMax) {
    return setupPreviewCanvas(canvas, ctx, dprMax, sizeKey);
  }

  async function loadImageTo(imgEl, url) {
    return new Promise(function (resolve, reject) {
      imgEl.onload = async function () {
        try { if (imgEl.decode) await imgEl.decode(); } catch (_) {}
        resolve();
      };
      imgEl.onerror = reject;
      imgEl.crossOrigin = "anonymous";
      imgEl.src = url;
    });
  }

  async function ensureFontLoaded(style) {
    style = style || DEFAULT_STYLE;
    if (!document.fonts || !document.fonts.load) return;
    var namePx = Math.round((style.nameSizePct || 0.0122) * DESIGN_W);
    await document.fonts.load("400 " + namePx + "px " + style.fontFamily, "ABCDEFGHIJKLMNOPQRSTUVWXYZÃ…Ã„Ã–");
    if (document.fonts.ready) await document.fonts.ready;
  }

  // ============================================================
  // 4) FLOWER REGISTRY
  // ============================================================
  const FLOWER_ASSETS = {};
  const FLOWER_OVERRIDES = {};
  let DEFAULT_FLOWER_KEY = null;
  const BG_KEY = "__bg";
  const TEXTURE_KEY = "__texture"; // paper-texture overlay image in imagesByKey

  function upsertFlower(key, opts) {
    opts = opts || {};
    var k = String(key || "");
    if (!k) return;
    FLOWER_ASSETS[k] = FLOWER_ASSETS[k] || { key: k, assetUrl: "", label: k };
    if (typeof opts.assetUrl === "string") FLOWER_ASSETS[k].assetUrl = opts.assetUrl;
    if (typeof opts.label === "string") FLOWER_ASSETS[k].label = opts.label;
    if (!DEFAULT_FLOWER_KEY) DEFAULT_FLOWER_KEY = k;
  }

  function setDefaultFlowerKey(key) {
    DEFAULT_FLOWER_KEY = String(key || "") || DEFAULT_FLOWER_KEY;
  }

  /**
   * Per-flower placement tuning -- just the image's own attach point now.
   *   stemAnchor: {x, y} in 0..1 -- default anchor point on the flower image (0.5/0.5 = center).
   *   The flower is drawn so this point lands on its slot's anchor position.
   *
   * The old per-slot-key override system (`p2c3:child2` style) is gone -- it existed to
   * work around the capped 2-parent/5-child layout. Position tuning now happens once, in
   * the hand-authored templates below, not per flower per slot-combination.
   */
  function upsertFlowerOverride(key, opts) {
    opts = opts || {};
    var k = String(key || "");
    if (!k) return;
    FLOWER_OVERRIDES[k] = FLOWER_OVERRIDES[k] || {};
    if (opts.stemAnchor && typeof opts.stemAnchor === "object") {
      var ax = (typeof opts.stemAnchor.x === "number") ? opts.stemAnchor.x : 0.5;
      var ay = (typeof opts.stemAnchor.y === "number") ? opts.stemAnchor.y : 0.5;
      FLOWER_OVERRIDES[k].stemAnchor = { x: ax, y: ay };
    }
  }

  function getFlowerOverride(key) {
    return FLOWER_OVERRIDES[String(key || "")] || {};
  }

  // ============================================================
  // 5) BOUQUET LAYOUT — hand-tuned anchor templates, not a formula
  // ============================================================
  function F(x, y, scale, rot, z) { return { shapeRole: "feature", x: x, y: y, scale: scale, rot: rot, z: z }; }
  function A(x, y, scale, rot, z) { return { shapeRole: "accent", x: x, y: y, scale: scale, rot: rot, z: z }; }

  // Positions from the reference-matching pass; SIZES are deliberately uniform -- every
  // slot starts at BASE_SLOT_SCALE (same as parent0's base) so size differences only
  // ever come from explicit merchant tuning, never from hidden per-slot defaults.
  const BASE_SLOT_SCALE = 1.38;
  const S = BASE_SLOT_SCALE;
  const DEFAULT_TEMPLATES = {
    1:  { source: "starter",   slots: [ F(0.50, 0.50, S, 0, 0) ] },
    2:  { source: "starter",   slots: [ F(0.41, 0.43, S, -0.05, 0), F(0.59, 0.60, S, 0.05, 1) ] },
    3:  { source: "reference", slots: [ A(0.58, 0.36, S, -0.1, 0), F(0.43, 0.41, S, -0.05, 1), F(0.48, 0.65, S, 0.05, 2) ] },
    4:  { source: "reference", slots: [ F(0.36, 0.32, S, -0.05, 0), F(0.62, 0.30, S, 0.05, 1), F(0.32, 0.59, S, -0.08, 2), F(0.63, 0.59, S, 0.08, 3) ] },
    5:  { source: "reference", slots: [ A(0.53, 0.31, S, -0.1, 0), F(0.39, 0.32, S, -0.05, 1), F(0.30, 0.57, S, -0.08, 2), F(0.65, 0.55, S, 0.08, 3), F(0.48, 0.66, S, 0, 4) ] },
    6:  { source: "starter",   slots: [ A(0.33, 0.27, S, -0.15, 0), F(0.41, 0.31, S, -0.05, 1), F(0.61, 0.30, S, 0.05, 2), F(0.32, 0.51, S, -0.06, 3), F(0.55, 0.49, S, 0.06, 4), F(0.46, 0.68, S, 0, 5) ] },
    7:  { source: "starter",   slots: [ A(0.35, 0.27, S, -0.15, 0), F(0.43, 0.28, S, -0.05, 1), F(0.61, 0.28, S, 0.05, 2), F(0.35, 0.46, S, -0.05, 3), F(0.57, 0.46, S, 0.05, 4), F(0.32, 0.64, S, -0.08, 5), F(0.57, 0.64, S, 0.08, 6) ] },
    8:  { source: "starter",   slots: [ A(0.33, 0.25, S, -0.15, 0), A(0.33, 0.28, S, -0.1, 1), F(0.44, 0.24, S, -0.05, 2), F(0.62, 0.24, S, 0.05, 3), F(0.41, 0.42, S, -0.05, 4), F(0.57, 0.42, S, 0.05, 5), F(0.32, 0.60, S, -0.1, 6), F(0.55, 0.62, S, 0.08, 7) ] },
    9:  { source: "reference", slots: [ A(0.30, 0.33, S, -0.15, 0), A(0.29, 0.45, S, -0.1, 1), F(0.59, 0.30, S, 0.08, 2), F(0.41, 0.32, S, -0.05, 3), F(0.42, 0.50, S, -0.05, 4), F(0.55, 0.48, S, 0.05, 5), F(0.32, 0.65, S, -0.1, 6), F(0.46, 0.70, S, -0.02, 7), F(0.59, 0.68, S, 0.05, 8) ] },
    10: { source: "starter",   slots: [ A(0.36, 0.27, S, -0.2, 0), A(0.45, 0.29, S, -0.1, 1), F(0.32, 0.43, S, -0.05, 2), F(0.55, 0.35, S, 0.05, 3), F(0.45, 0.42, S, -0.05, 4), F(0.65, 0.48, S, 0.08, 5), F(0.26, 0.59, S, -0.1, 6), F(0.48, 0.59, S, 0, 7), A(0.61, 0.63, S, 0.1, 8), F(0.52, 0.71, S, 0, 9) ] },
    11: { source: "reference", slots: [ A(0.40, 0.30, S, -0.2, 0), A(0.46, 0.30, S, -0.1, 1), F(0.32, 0.45, S, -0.05, 2), F(0.55, 0.36, S, 0.05, 3), F(0.45, 0.43, S, -0.05, 4), F(0.66, 0.50, S, 0.08, 5), F(0.25, 0.59, S, -0.1, 6), F(0.47, 0.59, S, 0, 7), A(0.61, 0.64, S, 0.1, 8), F(0.57, 0.59, S, 0.05, 9), F(0.50, 0.70, S, 0, 10) ] },
    12: { source: "starter",   slots: [ A(0.40, 0.30, S, -0.2, 0), A(0.46, 0.30, S, -0.1, 1), F(0.32, 0.45, S, -0.05, 2), F(0.55, 0.36, S, 0.05, 3), F(0.45, 0.42, S, -0.05, 4), F(0.67, 0.50, S, 0.08, 5), F(0.25, 0.59, S, -0.1, 6), F(0.48, 0.59, S, 0, 7), A(0.61, 0.64, S, 0.1, 8), F(0.58, 0.59, S, 0.05, 9), F(0.50, 0.70, S, 0, 10), F(0.72, 0.34, S, 0.1, 11) ] },
    // 13/14 are DEV-only counts (2026-07-31): the 12 baseline plus a left and a right
    // flank slot. Only reachable via an explicit "13"/"14" count-key patch -- real 13/14
    // the vase has no layout above p2c3 (6+ flowers use the species spots).
    13: { source: "starter",   slots: [ A(0.40, 0.30, S, -0.2, 0), A(0.46, 0.30, S, -0.1, 1), F(0.32, 0.45, S, -0.05, 2), F(0.55, 0.36, S, 0.05, 3), F(0.45, 0.42, S, -0.05, 4), F(0.67, 0.50, S, 0.08, 5), F(0.25, 0.59, S, -0.1, 6), F(0.48, 0.59, S, 0, 7), A(0.61, 0.64, S, 0.1, 8), F(0.58, 0.59, S, 0.05, 9), F(0.50, 0.70, S, 0, 10), F(0.72, 0.34, S, 0.1, 11), F(0.28, 0.34, S, -0.15, 12) ] },
    14: { source: "starter",   slots: [ A(0.40, 0.30, S, -0.2, 0), A(0.46, 0.30, S, -0.1, 1), F(0.32, 0.45, S, -0.05, 2), F(0.55, 0.36, S, 0.05, 3), F(0.45, 0.42, S, -0.05, 4), F(0.67, 0.50, S, 0.08, 5), F(0.25, 0.59, S, -0.1, 6), F(0.48, 0.59, S, 0, 7), A(0.61, 0.64, S, 0.1, 8), F(0.58, 0.59, S, 0.05, 9), F(0.50, 0.70, S, 0, 10), F(0.72, 0.34, S, 0.1, 11), F(0.28, 0.34, S, -0.15, 12), F(0.70, 0.66, S, 0.12, 13) ] }
  };

  // Hard layout ceiling: how many flowers a bouquet template can hold. 13/14 exist for
  // dev calibration only -- customer-facing caps come from the section/block settings.
  const MAX_LAYOUT_FLOWERS = 14;
  // At or below this, arrangements are hand-composed per count (v1 only); above it,
  // each flower brings its own spots and the v1/v2 repeat rules apply.
  const SMALL_ARRANGEMENT_MAX = 5;

  /*
   * THREE ADJUSTMENT LAYERS (coarse -> fine), all persistable via setTemplateOverrides:
   *
   *   1. Count baseline  -- key "5"      -- the 12 layouts above; used to derive every split.
   *   2. Split layout    -- key "p2c3"   -- 2 parents + 3 children is genuinely different
   *      geometry from 1+4 or 0+5. Splits are derived lazily from the count baseline
   *      (first P slots become parent0.., the rest child0..) and then tuned independently.
   *   3. Species offset  -- "flowers": { "<flowerKey>": { "p2c3:child2": {dx,dy,scale} } }
   *      -- a rose at child2 needs different fine-tuning than a marigold at child2,
   *      because every flower PNG has its own proportions/stem. dx/dy are fractions of
   *      the artboard; scale multiplies the slot's scale.
   *
   * Assignment is positional: parent i -> slot "parent<i>", child j -> slot "child<j>",
   * in the order the customer arranged them (drag-drop order = bouquet order).
   */

  // parent0's shared starting position for splits that have NO baked layout below --
  // stamped onto derived-from-count templates only. Baked splits own their parent0
  // (the merchant baseline deliberately raises/shrinks it as counts grow).
  const BASE_PARENT0 = { x: 0.416, y: 0.424, scale: 1.38, rot: -0.2 };

  // Split-specific built-in layouts. The p1c0/p2c0..p2c3 series is the merchant-tuned
  // baseline (calibrated in dev mode, baked 2026-07-04, re-baked 2026-07-12); "p1c2" is the "upward triangle"
  // single-parent starter. Facing values are informational -- resolve() recomputes
  // facing from the slot's x at render time.
  const DEFAULT_SPLITS = {
    "p1c0": { source: "merchant", slots: [
      { pos: "parent0", x: 0.416, y: 0.424, scale: 1.38, rot: -0.2,  z: 0 }
    ] },
    "p2c0": { source: "merchant", slots: [
      { pos: "parent0", x: 0.42, y: 0.48000000000000004, scale: 1.38, rot: -0.08000000000000003, z: 0 },
      { pos: "parent1", x: 0.602, y: 0.45600000000000007, scale: 1.38, rot: 0.09, z: 1 }
    ] },
    "p2c1": { source: "merchant", slots: [
      { pos: "parent0", x: 0.416, y: 0.424, scale: 1.38, rot: -0.2, z: 0 },
      { pos: "parent1", x: 0.602, y: 0.404, scale: 1.38, rot: 0.15, z: 1 },
      { pos: "child0", x: 0.54, y: 0.57, scale: 1.38, rot: -0.030000000000000013, z: 2 }
    ] },
    "p2c2": { source: "merchant", slots: [
      { pos: "parent0", x: 0.416, y: 0.424, scale: 1.38, rot: -0.2,  z: 0 },
      { pos: "parent1", x: 0.602, y: 0.404, scale: 1.38, rot: 0.15,  z: 1 },
      { pos: "child0",  x: 0.424, y: 0.57,  scale: 1.38, rot: -0.31, z: 2 },
      { pos: "child1",  x: 0.602, y: 0.55,  scale: 1.38, rot: 0.32,  z: 3 }
    ] },
    "p2c3": { source: "merchant", slots: [
      { pos: "parent0", x: 0.416, y: 0.424, scale: 1.38, rot: -0.2,  z: 0 },
      { pos: "parent1", x: 0.602, y: 0.404, scale: 1.38, rot: 0.15,  z: 1 },
      { pos: "child0",  x: 0.38,  y: 0.55,  scale: 1.38, rot: -0.31, z: 2 },
      { pos: "child1",  x: 0.518, y: 0.602, scale: 1.38, rot: -0.04, z: 3 },
      { pos: "child2",  x: 0.644, y: 0.536, scale: 1.38, rot: 0.36,  z: 4 }
    ] },
  };

  // ---- No inherited splits on the vase --------------------------------------
  // The bouquet derives 13/14-flower layouts from its tuned p2c10. The vase has no
  // p2c10 and no layout above p2c3: 1-5 flowers use the count layouts, 6+ are placed
  // by the species spots (month_key:flowerN). Nothing inherits, so the map is empty
  // and inheritedOffsetKey() returns null for every key -- which is what it already
  // did here, since p2c11/p2c12 were never built.
  const INHERIT_SPLIT = {};
  const INHERIT_POS = { child10: "child6", child11: "child5" };
  const EXTRA_SLOTS_OVER_12 = [
    { x: 0.306, y: 0.44,  scale: 1.12, rot: -0.28 },  // 13th -- left flank
    { x: 0.696, y: 0.494, scale: 1.12, rot: 0.28 }    // 14th -- right flank
  ];

  /** "p2c11:child3" -> "p2c10:child3" (and child10/11 -> their neighbour). Null when the
   *  key belongs to a split that inherits nothing, i.e. everything at 12 flowers or less. */
  function inheritedOffsetKey(offsetKey) {
    var s = String(offsetKey || "");
    var i = s.indexOf(":");
    if (i < 0) return null;
    var src = INHERIT_SPLIT[s.slice(0, i)];
    if (!src) return null;
    var pos = s.slice(i + 1);
    return src + ":" + (INHERIT_POS[pos] || pos);
  }

  function splitKeyFor(p, c) { return "p" + (p | 0) + "c" + (c | 0); }

  // ---- Working state: mutable clones so dev-mode nudges persist across re-renders ----
  function cloneTemplate(t) { return { source: t.source, slots: t.slots.map(function (s) { return Object.assign({}, s); }) }; }
  var WORKING = null;          // { counts: {"1"..: tpl}, splits: {"p2c3": tpl, ...lazy} }
  var SPECIES_OFFSETS = {};
  function ensureWorking() {
    if (WORKING) return;
    WORKING = { counts: {}, splits: {} };
    Object.keys(DEFAULT_TEMPLATES).forEach(function (k) { WORKING.counts[k] = cloneTemplate(DEFAULT_TEMPLATES[k]); });
  }
  function clamp01(v) { return Math.max(0, Math.min(1, v)); }

  function parseSplitKey(splitKey) {
    var m = /^p(\d+)c(\d+)$/.exec(String(splitKey || ""));
    return m ? { p: parseInt(m[1], 10), c: parseInt(m[2], 10) } : null;
  }

  /**
   * Derive a split template (e.g. p2c3) from a base: the built-in split layout if one
   * exists, else the count baseline with the first P slots tagged parent0.., rest child0...
   * `fromFactory` derives from the pristine built-ins (for dirty-diffing); otherwise from
   * the working count baselines so count-level overrides propagate into new splits.
   */
  function deriveSplitTemplate(p, c, fromFactory) {
    var key = splitKeyFor(p, c);
    var t;
    var n = Math.max(1, Math.min(MAX_LAYOUT_FLOWERS, p + c));
    // Same-total aliasing (merchant decision 2026-07-04): a split WITHOUT its own baked
    // layout reuses the baseline for the same TOTAL flower count -- p0c4 renders like
    // p2c2 (both 4 flowers), p1c3 too; slots are retagged positionally (slot order =
    // parent0.., child0..). A baked entry (p1c2's triangle, or any future p1cX tuning)
    // always wins over the alias.
    var canonical = n === 1 ? "p1c0" : splitKeyFor(2, n - 2);
    if (DEFAULT_SPLITS[key]) {
      t = cloneTemplate(DEFAULT_SPLITS[key]);
    // 13-14 flower DEV counts came from the bouquet, where they cloned p2c10 and
    // appended flank slots. The vase has no p2c10 and never asks for one: 1-5 use the
    // count layouts, 6+ go through the species spots, so nothing above p2c3 is built.
    } else if (DEFAULT_SPLITS[canonical]) {
      t = cloneTemplate(DEFAULT_SPLITS[canonical]);
      t.slots.forEach(function (s, i) {
        s.pos = i < p ? ("parent" + i) : ("child" + (i - p));
      });
    } else {
      ensureWorking();
      var base = fromFactory ? DEFAULT_TEMPLATES[String(n)] : (WORKING.counts[String(n)] || DEFAULT_TEMPLATES[String(n)]);
      t = cloneTemplate(base);
      t.slots.forEach(function (s, i) {
        s.pos = i < p ? ("parent" + i) : ("child" + (i - p));
        delete s.shapeRole;
      });
      // Count-derived splits start parent0 from the shared base position; baked and
      // aliased splits above own their parent0.
      t.slots.forEach(function (s) { if (s.pos === "parent0") Object.assign(s, BASE_PARENT0); });
    }
    return t;
  }

  function getWorkingSplit(splitKey) {
    ensureWorking();
    if (!WORKING.splits[splitKey]) {
      var pc = parseSplitKey(splitKey);
      if (!pc) return null;
      WORKING.splits[splitKey] = deriveSplitTemplate(pc.p, pc.c, false);
    }
    return WORKING.splits[splitKey];
  }

  /** Nudge one slot's position in a split's working template (dev mode). */
  function nudgeSlot(splitKey, tplIndex, dx, dy) {
    var t = getWorkingSplit(splitKey);
    var slot = t && t.slots[tplIndex];
    if (!slot) return null;
    slot.x = clamp01(slot.x + dx);
    slot.y = clamp01(slot.y + dy);
    return { x: slot.x, y: slot.y };
  }

  /** Grow/shrink one slot's flower in a split's working template (dev mode). Clamped 0.3..2.5. */
  function nudgeSlotScale(splitKey, tplIndex, delta) {
    var t = getWorkingSplit(splitKey);
    var slot = t && t.slots[tplIndex];
    if (!slot) return null;
    slot.scale = Math.max(0.3, Math.min(2.5, (slot.scale || 1) + delta));
    return { scale: slot.scale };
  }

  /** Rotate one slot's flower in a split's working template (dev mode). Radians, clamped Â±1.5 (~Â±86Â°). */
  function nudgeSlotRot(splitKey, tplIndex, delta) {
    var t = getWorkingSplit(splitKey);
    var slot = t && t.slots[tplIndex];
    if (!slot) return null;
    slot.rot = Math.max(-1.5, Math.min(1.5, (slot.rot || 0) + delta));
    return { rot: slot.rot };
  }

  /**
   * Per-species fine-tuning at one position of one split, e.g. Rose at p2c3:child2.
   * dx/dy accumulate as fractions of the artboard (clamped Â±0.3); dScale multiplies
   * the offset's scale factor (clamped 0.5..2); dRot adds radians (clamped Â±1.5).
   */
  function nudgeFlowerOffset(flowerKey, offsetKey, ddx, ddy, dScale, dRot, ddz) {
    var fk = canonKey(flowerKey);
    if (!fk) return null;
    SPECIES_OFFSETS[fk] = SPECIES_OFFSETS[fk] || {};
    // First nudge at an inheriting 13/14 position MATERIALISES the 12-flower value it has
    // been rendering with, so the flower moves by the nudge instead of snapping to zero.
    var inh = inheritedOffsetKey(offsetKey);
    if (inh && !SPECIES_OFFSETS[fk][offsetKey] && SPECIES_OFFSETS[fk][inh]) {
      SPECIES_OFFSETS[fk][offsetKey] = Object.assign({ dx: 0, dy: 0, scale: 1, rot: 0 }, SPECIES_OFFSETS[fk][inh]);
    }
    var off = SPECIES_OFFSETS[fk][offsetKey] = SPECIES_OFFSETS[fk][offsetKey] || { dx: 0, dy: 0, scale: 1, rot: 0 };
    off.dx = Math.max(-0.3, Math.min(0.3, off.dx + (ddx || 0)));
    off.dy = Math.max(-0.3, Math.min(0.3, off.dy + (ddy || 0)));
    if (dScale) off.scale = Math.max(0.5, Math.min(2, (off.scale || 1) + dScale));
    if (dRot) off.rot = Math.max(-1.5, Math.min(1.5, (off.rot || 0) + dRot));
    // dz = manual draw-order bias (integer "layers"). +1 forces this flower in FRONT of
    // every flower with a lower dz regardless of height; -1 sends it behind. Overrides the
    // default painter's-by-height order without moving the flower.
    if (ddz) off.dz = Math.max(-20, Math.min(20, Math.round((off.dz || 0) + ddz)));
    if (!off.dz) delete off.dz; // keep JSON clean when neutral
    // An inheriting 13/14 key keeps its explicit neutral entry -- dropping it would fall
    // straight back to the 12-flower value the merchant just nudged away from.
    if (!inh && !off.dx && !off.dy && off.scale === 1 && !off.rot && !off.dz && !off.flip) { // fully neutral -> drop it
      delete SPECIES_OFFSETS[fk][offsetKey];
      if (!Object.keys(SPECIES_OFFSETS[fk]).length) delete SPECIES_OFFSETS[fk];
      return { dx: 0, dy: 0, scale: 1, rot: 0, dz: 0 };
    }
    return Object.assign({}, off);
  }

  // Localized month keys -> canonical English key. Tuning (offsets/anchors/scales) is
  // stored under English month names, but localized storefronts feed localized flower
  // keys (.se Swedish "juni", .de German "juni"/"märz", etc.), which would otherwise miss
  // ALL tuning and fall back to the bare baseline. Normalize on every lookup.
  var MONTH_ALIAS = {
    januari: "january", januar: "january",
    februari: "february", februar: "february",
    mars: "march", "märz": "march", marz: "march",
    maj: "may", mai: "may",
    juni: "june",
    juli: "july",
    augusti: "august",
    oktober: "october",
    dezember: "december", desember: "december", december: "december"
  };
  // Norwegian and Danish month names join the Swedish and German ones above. These exist
  // because a flower_key is a THEME SETTING of type text, which Shopify exposes to
  // Translate & Adapt with no way to opt out -- so a key can come back translated
  // ("juni", "desember") in a non-default locale. Canonicalising here means a translated
  // MONTH key still resolves to the same flower. Keys that are flower names rather than
  // months (aster, carnation, rose) have no such safety net: those must be left
  // untranslated in Translate & Adapt.
  // A month can carry up to four paintings, and each keeps its OWN offset bucket:
  //   june            the month's first birth flower
  //   june__v2        the same flower painted again (used when a month repeats)
  //   june__b         the month's OTHER birth flower, a different species
  //   june__b__v2     that second species, painted again
  // The MONTH part still has to canonicalise: on a localized storefront the key arrives
  // as "juni__b__v2" and must land on "june__b__v2" rather than create an unreachable
  // duplicate (the LOCALE-KEY rule). Suffixes are peeled off, the month is mapped, and
  // the suffixes go back on in the same order.
  function canonKey(k) {
    k = String(k || "").toLowerCase();
    var suffix = "";
    var m = /(__v\d+)$/.exec(k);
    if (m) { suffix = m[1]; k = k.slice(0, -m[1].length); }
    if (/__b$/.test(k)) { suffix = "__b" + suffix; k = k.slice(0, -3); }
    return (MONTH_ALIAS[k] || k) + suffix;
  }

  // Mirror one flower at one position. Lives with the other per-position offsets, so it
  // travels in the adjustments JSON and ships on the order like every other tuning value.
  // Write a flower's ABSOLUTE spot for one of its own counts. Seeded from wherever it is
  // currently standing, so the first nudge moves it instead of snapping it to the origin.
  /** The stored spot for month_key:flowerN, with the defaults an untuned one renders at
   *  filled in -- so an absolute edit can be seeded from what you are looking at. */
  function getSpeciesSpot(flowerKey, index) {
    var byKey = SPECIES_OFFSETS[canonKey(flowerKey)] || {};
    var s = byKey["flower" + index] || {};
    return {
      x: (typeof s.x === "number") ? s.x : null,
      y: (typeof s.y === "number") ? s.y : null,
      scale: (typeof s.scale === "number") ? s.scale : defaultSpotScale(),
      rot: (typeof s.rot === "number") ? s.rot : 0,
      z: (typeof s.z === "number") ? s.z : null,
      dz: (typeof s.dz === "number") ? s.dz : 0,
      facing: s.facing || null,
      flip: !!s.flip
    };
  }

  function setSpeciesSpot(flowerKey, index, patch) {
    var fk = canonKey(flowerKey);
    if (!fk) return null;
    SPECIES_OFFSETS[fk] = SPECIES_OFFSETS[fk] || {};
    var k = "flower" + index;
    var spot = SPECIES_OFFSETS[fk][k] = SPECIES_OFFSETS[fk][k] || {};
    if (patch && typeof patch.x === "number") spot.x = Math.max(-0.5, Math.min(1.5, patch.x));
    if (patch && typeof patch.y === "number") spot.y = Math.max(-0.5, Math.min(1.5, patch.y));
    if (patch && typeof patch.scale === "number") spot.scale = Math.max(0.2, Math.min(4, patch.scale));
    if (patch && typeof patch.rot === "number") spot.rot = Math.max(-1.5, Math.min(1.5, patch.rot));
    if (patch && typeof patch.z === "number") spot.z = patch.z;
    // dz is the LAYER, and it is the only thing that reorders drawing. z is merely the
    // tiebreak between two flowers at the exact same height, so writing z looked like
    // a z-order control and did nothing.
    if (patch && typeof patch.dz === "number") {
      var _dz = Math.max(-20, Math.min(20, Math.round(patch.dz)));
      if (_dz) spot.dz = _dz; else delete spot.dz;
    }
    if (patch && typeof patch.flip === "boolean") spot.flip = patch.flip;
    // A spot records which way it should FACE, not whether it happens to be mirrored.
    // "flip: true" says a mirror was applied without saying what it was for; a facing
    // survives the painting being redrawn the other way round, because the mirror is
    // recomputed from it rather than frozen into it.
    if (patch && (patch.facing === "left" || patch.facing === "right")) spot.facing = patch.facing;
    else if (patch && patch.facing === null) delete spot.facing;
    return Object.assign({}, spot);
  }

  /** Set ONE position's mirror outright, rather than toggling and hoping. The per-painting
   *  flip mirrors the artwork everywhere it appears; this mirrors it at one position only,
   *  which is what "flower1 faces right, flower2 faces left" needs. */
  /** The facing ONE position wants, for the 1-5 layouts. Same idea as a spot's facing. */
  function setOffsetFacing(flowerKey, offsetKey, dir) {
    var fk = canonKey(flowerKey);
    if (!fk || !offsetKey) return null;
    SPECIES_OFFSETS[fk] = SPECIES_OFFSETS[fk] || {};
    var off = SPECIES_OFFSETS[fk][offsetKey] = SPECIES_OFFSETS[fk][offsetKey] || { dx: 0, dy: 0, scale: 1, rot: 0 };
    if (dir === "left" || dir === "right") off.facing = dir; else delete off.facing;
    delete off.flip;   // the stated facing replaces the recorded mirror
    return off.facing || null;
  }

  function setOffsetFlip(flowerKey, offsetKey, on) {
    var fk = canonKey(flowerKey);
    if (!fk || !offsetKey) return false;
    SPECIES_OFFSETS[fk] = SPECIES_OFFSETS[fk] || {};
    var off = SPECIES_OFFSETS[fk][offsetKey] = SPECIES_OFFSETS[fk][offsetKey] || { dx: 0, dy: 0, scale: 1, rot: 0 };
    if (on) off.flip = true; else delete off.flip;
    return !!on;
  }

  function toggleFlowerFlip(flowerKey, offsetKey) {
    var fk = canonKey(flowerKey);
    if (!fk || !offsetKey) return false;
    SPECIES_OFFSETS[fk] = SPECIES_OFFSETS[fk] || {};
    var off = SPECIES_OFFSETS[fk][offsetKey] = SPECIES_OFFSETS[fk][offsetKey] || { dx: 0, dy: 0, scale: 1, rot: 0 };
    off.flip = !off.flip;
    return !!off.flip;
  }

  // TWO LAYERS, because a flower mostly sits where it sits, and only sometimes needs a
  // nudge for one particular arrangement.
  //
  //   "parent0"        BASE  -- June as flower nr 1, in every setup
  //   "p2c3:parent0"   SETUP -- a small correction, only when it is 2 parents + 3 children
  //
  // The base used not to exist: every value was split-keyed, so June at parent0 was a
  // separate number for each of the thirteen counts and had to be tuned thirteen times
  // over. Now the base carries the position and the setup layer carries the exception.
  //
  // They COMPOUND rather than override: offsets and rotation add, scale multiplies, flips
  // XOR. So moving the base moves the flower in every arrangement at once and the
  // per-setup corrections stay the small deltas they are, instead of each one silently
  // pinning a full position.
  //
  // Existing tuning is all split-keyed, which lands in the SETUP layer with an empty base
  // -- so every poster renders exactly as it did, and the base layer starts clean.
  // The base layer is keyed by the flower's NUMBER in the arrangement -- "june" ->
  // "flower1" -- not by the slot name. parent0/child0 is bouquet vocabulary: a vase has
  // no parents, just flowers 1, 2, 3. The number has to come from the SPLIT, not from the
  // slot name alone, because child0 is flower 3 when there are two parents and flower 2
  // when there is one.
  function baseKeyForSlot(splitKey, pos) {
    var p = String(pos || "");
    var m = /^parent(\d+)$/.exec(p);
    if (m) return "flower" + (parseInt(m[1], 10) + 1);
    m = /^child(\d+)$/.exec(p);
    if (m) {
      var pc = parseSplitKey(splitKey);
      var parents = pc ? pc.p : 2;
      return "flower" + (parents + parseInt(m[1], 10) + 1);
    }
    return p;
  }

  // ---- Repeat rules -------------------------------------------------------------
  // What a SECOND painting of the same flower means differs per flower:
  //
  //   "alternate"  v2 is the same bloom painted again, so repeats alternate to stop a
  //                month looking copy-pasted:   1:v1  2:v1+v2  3:v1+v2+v1  4:v1+v2+v1+v2
  //   "pair"       v2 is a painting of TWO blooms, so a count is built from doubles plus
  //                at most one single:          1:v1  2:v2  3:v1+v2  4:v2+v2  5:v1+v2+v2
  //
  // Under "pair" the number of DRAWN flowers stops matching the number of PEOPLE -- four
  // Junes draw as two images. The captions still list four people, which is why the text
  // block reads input.captions rather than counting what was drawn.
  //
  // Resolved at RENDER time, not when a flower is added: which paintings are used depends
  // on how many there are in the end, and that changes with every addition and removal.
  //   entries: [{ key, ... }] in the customer's order
  //   opts.ruleFor(key)  -> "alternate" | "pair"
  //   opts.v2KeyFor(key) -> the v2 registry key, or null when none is uploaded
  //   opts.maxFor(key)   -> most images to DRAW for this flower, 0 = no limit. Some
  //                         paintings already hold a whole spray of blooms, so a second
  //                         copy adds nothing: four people pick it, one image is drawn,
  //                         and all four are still named. The cap applies AFTER the
  //                         repeat rule, since it limits images and the rule decides
  //                         which images those are.
  function resolveRepeats(entries, opts) {
    entries = entries || [];
    opts = opts || {};
    var ruleFor = opts.ruleFor || function () { return "alternate"; };
    var v2KeyFor = opts.v2KeyFor || function () { return null; };
    var maxFor = opts.maxFor || function () { return 0; };

    // Count and cap by the BASE flower: under "alternate" the keys are already pinned to
    // v1/v2, and counting those separately would let a month slip past its own limit.
    function baseOf(k) { k = String(k || ""); return k.slice(-4) === "__v2" ? k.slice(0, -4) : k; }

    // 1-5 flowers are hand-composed arrangements and use v1 ONLY: no pairs, no
    // alternating. A v2 key pinned when the flower was added (the storefront alternates
    // at add time) is mapped back to v1 here, so nothing upstream can sneak a v2 in.
    if (entries.filter(function (e) { return e && e.key; }).length <= SMALL_ARRANGEMENT_MAX) {
      var shown = {};
      return entries.filter(function (e) { return e && e.key; }).reduce(function (out, e) {
        var b = baseOf(e.key), cap = maxFor(b) || 0;
        if (cap > 0 && (shown[b] || 0) >= cap) return out;  // spray paintings still draw once
        shown[b] = (shown[b] || 0) + 1;
        var v1 = Object.assign({}, e, { key: b });
        if (v1.flowerKey) v1.flowerKey = baseOf(v1.flowerKey);
        if (v1.stemKey) v1.stemKey = "stem:" + b;
        delete v1.pairs;
        out.push(v1);
        return out;
      }, []);
    }

    var counts = {};
    entries.forEach(function (e) {
      if (e && e.key) { var b = baseOf(e.key); counts[b] = (counts[b] || 0) + 1; }
    });

    var drawn = [], seen = {}, made = {};
    entries.forEach(function (e) {
      if (!e || !e.key) return;
      var base = baseOf(e.key);
      var cap = maxFor(base) || 0;
      var rule = ruleFor(base);
      var v2 = v2KeyFor(base);

      // No pair paintings for MEDIUMS (user 2026-10-23/24: "ignore pair on medium", "medium are
      // still showing pairs"): every person of a medium month gets their own painting (v1), the same
      // as at 1-5. Larges and smalls keep the pair rule. The class is looked up by the FLOWER
      // (flowerKey, e.g. "january"): e.key is the block id on a real poster, which has no class
      // and read as M, so no flower ever made a pair (2026-10-25).
      if (rule === "pair" && v2 && getVaseClass(baseOf(e.flowerKey || e.key)) === "M") {
        if (cap > 0 && (made[base] || 0) >= cap) return;
        made[base] = (made[base] || 0) + 1;
        var single = Object.assign({}, e, { key: base });
        if (single.flowerKey) single.flowerKey = baseOf(single.flowerKey);
        if (single.stemKey) single.stemKey = "stem:" + base;
        delete single.pairs;
        drawn.push(single);
        return;
      }
      if (rule === "pair" && v2) {
        // Emit this flower's whole set once, at its FIRST appearance, so the group stays
        // together in the arrangement instead of scattering through the other flowers.
        if (seen[base]) return;
        seen[base] = true;
        var n = counts[base];
        var doubles = Math.floor(n / 2);
        var set = [];
        if (n % 2 === 1) set.push(Object.assign({}, e));                          // the odd single
        // The double is a different PAINTING, so it takes the v2 flowerKey too. Swapping
        // only the image key left flowerKey on v1, and every spot, scale and anchor is
        // looked up by flowerKey: the pair stood on the single's "june:flower1" spot (and on
        // "flower2" once a third June made the single flower1), so tuning one moved the other.
        var v2fk = e.flowerKey ? baseOf(e.flowerKey) + "__v2" : v2;
        for (var i = 0; i < doubles; i++) set.push(Object.assign({}, e, { key: v2, flowerKey: v2fk, pairs: 2 }));
        if (cap > 0 && set.length > cap) set = set.slice(0, cap);
        set.forEach(function (x) { drawn.push(x); });
        return;
      }

      if (cap > 0 && (made[base] || 0) >= cap) return; // this painting already says enough
      made[base] = (made[base] || 0) + 1;
      drawn.push(e);
    });
    return drawn;
  }

  function combineOffsets(base, setup) {
    if (!base && !setup) return { dx: 0, dy: 0, scale: 1, rot: 0, dz: 0, flip: false };
    var b = Object.assign({ dx: 0, dy: 0, scale: 1, rot: 0, dz: 0, flip: false }, base || {});
    if (!setup) return b;
    var s = setup;
    return {
      dx: b.dx + (s.dx || 0),
      dy: b.dy + (s.dy || 0),
      scale: (b.scale || 1) * (typeof s.scale === "number" ? s.scale : 1),
      rot: (b.rot || 0) + (s.rot || 0),
      dz: (b.dz || 0) + (s.dz || 0),
      flip: !!b.flip !== !!s.flip
    };
  }

  // One flower's offset at one exact key -- no base layer, no combining.
  function getFlowerOffsetAt(flowerKey, key) {
    var byKey = SPECIES_OFFSETS[canonKey(flowerKey)];
    var off = byKey && byKey[key];
    if (!off && byKey) {
      var inh = inheritedOffsetKey(key);
      if (inh) off = byKey[inh];
    }
    return off ? Object.assign({ rot: 0, dz: 0, flip: false }, off) : { dx: 0, dy: 0, scale: 1, rot: 0, dz: 0, flip: false };
  }

  function getFlowerOffset(flowerKey, splitKey, pos) {
    var byKey = SPECIES_OFFSETS[canonKey(flowerKey)];
    if (!byKey) return combineOffsets(null, null);
    var setupKey = splitKey ? (splitKey + ":" + pos) : null;
    var setup = setupKey && byKey[setupKey];
    // Untuned 13/14 positions read the 12-flower correction (see INHERIT_SPLIT).
    if (!setup && setupKey) {
      var inh = inheritedOffsetKey(setupKey);
      if (inh) setup = byKey[inh];
    }
    return combineOffsets(byKey[baseKeyForSlot(splitKey, pos)], setup);
  }

  /** Remove one species' offset at one position entirely (back to the plain slot). */
  function clearFlowerOffset(flowerKey, offsetKey) {
    var fk = canonKey(flowerKey);
    if (SPECIES_OFFSETS[fk]) {
      delete SPECIES_OFFSETS[fk][offsetKey];
      if (!Object.keys(SPECIES_OFFSETS[fk]).length) delete SPECIES_OFFSETS[fk];
    }
  }

  // ---- Per-species visual-center overrides (dev-set, persisted in the adjustments JSON).
  // The center is where the flower head actually sits in the image (assets are rarely
  // centered); it's the placement anchor AND the pivot for rotation and flipping.
  // Falls back to the block-settings anchor when no dev override exists.
  // Baked species centers (merchant-calibrated). Dev-mode clicks and pasted
  // overrides update/extend this map at runtime.
  var ANCHOR_OVERRIDES = {};
  function setFlowerAnchor(flowerKey, x, y) {
    var fk = canonKey(flowerKey);
    if (!fk) return null;
    ANCHOR_OVERRIDES[fk] = { x: clamp01(Number(x) || 0.5), y: clamp01(Number(y) || 0.5) };
    return Object.assign({}, ANCHOR_OVERRIDES[fk]);
  }
  function getFlowerAnchor(flowerKey) {
    var a = ANCHOR_OVERRIDES[canonKey(flowerKey)];
    return a ? Object.assign({}, a) : null;
  }

  // ---- Per-species default SCALE multiplier (the size sibling of the anchor map).
  // Applied to that flower type at EVERY position, multiplied on top of the slot
  // scale and any per-position species offset. 1 = neutral (not stored). Persisted
  // in the adjustments JSON as "scales": { "<flowerKey>": 1.15, ... }.
  var SPECIES_SCALES = {};
  function setFlowerScale(flowerKey, s) {
    var fk = canonKey(flowerKey);
    if (!fk) return 1;
    var v = Number(s);
    if (!(v > 0)) v = 1;
    v = Math.max(0.3, Math.min(3, v));
    if (Math.abs(v - 1) < 1e-9) delete SPECIES_SCALES[fk];
    else SPECIES_SCALES[fk] = v;
    return v;
  }
  // Per-flower default ROTATION -- the sibling of the default scale above. Applied to
  // that painting everywhere it appears, so each version (june, june__v2, june__b) can
  // sit at its own angle without touching any position.
  var SPECIES_ROTATIONS = {};

  // A flower block's "Flower direction" setting is the painting'"'"'s DEFAULT facing: set it to
  // left and the artwork is mirrored everywhere, without touching a single position. It
  // went inert when automatic flipping was removed -- facing and direction became the
  // same value, so the comparison that used to flip could never be true. It is read
  // directly now, and XORs with the per-painting flip below and any per-position flip.
  //
  // Whether a painting is mirrored. This is a decision about the ARTWORK -- it faces the
  // wrong way -- so it belongs to the painting, not to a position or a flower count.
  // Keyed per position it was forgotten the moment a flower was added, because the
  // position key changed with the count.
  var SPECIES_FLIPS = {};
  function setFlowerFlip(flowerKey, on) {
    var fk = canonKey(flowerKey);
    if (!fk) return false;
    if (on) SPECIES_FLIPS[fk] = true;
    else delete SPECIES_FLIPS[fk];
    return !!on;
  }
  function getFlowerFlip(flowerKey) { return !!SPECIES_FLIPS[canonKey(flowerKey)]; }

  // ORIGINAL FACING: which way the artwork itself is drawn. This is a STATEMENT OF FACT
  // about the painting, not an instruction -- setting it moves nothing. It exists so that
  // a position which wants a flower facing right can be compared against how the flower
  // is actually painted, and the flip decided from that rather than by eye.
  //
  // It deliberately does NOT mirror on its own. When it did, "this is painted facing
  // right" and "make this face right" were the same value, so recording the truth about
  // an asset silently flipped it.
  var SPECIES_DIRECTIONS = {};
  function setFlowerDirection(flowerKey, dir) {
    var fk = canonKey(flowerKey);
    if (!fk) return null;
    if (dir === "left" || dir === "right") SPECIES_DIRECTIONS[fk] = dir;
    else delete SPECIES_DIRECTIONS[fk];
    return SPECIES_DIRECTIONS[fk] || null;
  }
  function getFlowerDirection(flowerKey) { return SPECIES_DIRECTIONS[canonKey(flowerKey)] || null; }

  // WHICH WAY A POSITION WANTS ITS FLOWER TO FACE, keyed "p2c0:parent1". This is the
  // other half of the facing model: the painting records how it is DRAWN, the slot
  // records what it NEEDS, and the mirror is the difference between them --
  //
  //     flip = slot.wants !== painting.isDrawn
  //
  // so a flower dropped into a right-facing slot turns itself the right way round
  // whatever it happens to be painted as, and no longer has to be flipped by hand.
  // Only the 1-5 layouts have slots; 6+ positions belong to the flower, not to a place.
  var SLOT_FACINGS = {};
  function setSlotFacing(slotKey, dir) {
    if (!slotKey) return null;
    if (dir === "left" || dir === "right") SLOT_FACINGS[slotKey] = dir;
    else delete SLOT_FACINGS[slotKey];
    return SLOT_FACINGS[slotKey] || null;
  }
  function getSlotFacing(slotKey) { return SLOT_FACINGS[slotKey] || null; }
  /** The facing actually used: the painting's override if it has one, else the block's. */
  function facingFor(flowerKey, blockDirection) {
    return getFlowerDirection(flowerKey) || (blockDirection === "left" ? "left" : "right");
  }
  function setFlowerRotation(flowerKey, r) {
    var fk = canonKey(flowerKey);
    if (!fk) return null;
    var v = Math.max(-1.5, Math.min(1.5, Number(r) || 0));
    if (Math.abs(v) < 1e-9) delete SPECIES_ROTATIONS[fk];
    else SPECIES_ROTATIONS[fk] = v;
    return v;
  }
  function getFlowerRotation(flowerKey) {
    var v = SPECIES_ROTATIONS[canonKey(flowerKey)];
    return (typeof v === "number") ? v : 0;
  }

  function getFlowerScale(flowerKey) {
    var v = SPECIES_SCALES[canonKey(flowerKey)];
    return (typeof v === "number" && v > 0) ? v : 1;
  }

  // ---- Artboard margin overrides (the colored rect's frame within the poster). Values are
  // fractions: top/footerHeight/footerGap of design HEIGHT, side of design WIDTH. Persisted
  // in the adjustments JSON as "margins": { top, side, footerHeight, footerGap }. Empty map
  // = the baked DEFAULT_STYLE margins. "top" moves the rect down; "footer*" set the bottom
  // band (holds names); "side" is the symmetric left/right inset.
  var MARGIN_OVERRIDES = {}; // subset of style keys: marginTopPct/marginSidePct/footerHeightPct/footerGapPct
  var MARGIN_KEYMAP = { top: "marginTopPct", side: "marginSidePct", footerHeight: "footerHeightPct", footerGap: "footerGapPct" };
  function setMargins(m) {
    if (!m || typeof m !== "object") return;
    Object.keys(MARGIN_KEYMAP).forEach(function (k) {
      var v = Number(m[k]);
      if (isFinite(v) && v >= 0 && v < 0.5) MARGIN_OVERRIDES[MARGIN_KEYMAP[k]] = v;
    });
  }
  function getMargins() { // always the FULL set (override else baked default) for the dev UI
    var out = {};
    Object.keys(MARGIN_KEYMAP).forEach(function (k) {
      var sk = MARGIN_KEYMAP[k];
      out[k] = (typeof MARGIN_OVERRIDES[sk] === "number") ? MARGIN_OVERRIDES[sk] : DEFAULT_STYLE[sk];
    });
    return out;
  }
  function getMarginOverridesJson() { // only the keys that differ (for getAdjustmentsJson)
    var mo = {};
    Object.keys(MARGIN_KEYMAP).forEach(function (k) {
      var sk = MARGIN_KEYMAP[k];
      if (typeof MARGIN_OVERRIDES[sk] === "number") mo[k] = MARGIN_OVERRIDES[sk];
    });
    return mo;
  }

  // ============================================================
  // 5b) THE SIZE-CLASS CELL MODEL (6+ people 2026-10-20, every count 1-12 since 2026-10-21)
  // ============================================================
  // From six PEOPLE up nobody hand-places every flower in every mix. Each painting has a
  // size class, and the class decides who stands where:
  //
  //   L  (tall)    its OWN fixed spot per occurrence ("july:flower1", "july:flower2"): the
  //                same spot in every mix. Untuned, it starts in the large row.
  //   S  (short)   the same, and it starts in the small row, bottom front, drawn on top.
  //   M  (body)    the Nth medium takes slot N of the CELL, named after the mix of
  //                larges and smalls actually DRAWN ("L1S0": one large, no smalls). An
  //                optional nudge per painting per cell sits on top. Untuned or too few
  //                slots: the ring rule (a ring around the larges, open at the bottom).
  //
  // Every stem is turned by itself toward one AIM point under the vase opening, and a
  // head never stands further from it than its stem reaches. Rotating stems by hand is
  // gone; a rotation nudge stays for the odd exception.
  //
  // Every poster that states its people count (renderPoster always does) is laid out this
  // way, 1 to 12 people. Matilda's hand-composed 1-5 layouts are the per-count medium slots
  // of the cells (cells.L0S0.byCount, see getCellSlots). Pair paintings follow the PEOPLE
  // (input.captions): only from six people. A caller without a people count still gets the
  // hand-composed layouts above at 1-5 paintings.
  // All positions are fractions of the panel, like every other slot.
  var SIZE_CLASSES = { S: true, M: true, L: true };
  var BOUQUET_CX = 0.585;    // centre line of the bouquet (panel width fraction)
  var BOUQUET_CY = 0.43;     // centre of the larges' core (panel height fraction)
  var PANEL_AR = 1.1655;     // panel height / width, so the ring is round on the poster
  // Deep inside the vase, not at its rim: aimed at the rim (y 0.68) the low side flowers got
  // shallow stems that crossed the opening and stuck out beside the vase (checked on the real
  // grid with the real paintings, 2026-10-21); at 0.80 they run down into it.
  var DEFAULT_AIM = { x: 0.585, y: 0.80 };
  var REFERENCE_VASE_MOUTH = { x: 0.48, y: 0.60 };   // the placement DEFAULT_AIM was drawn for
  var VASE_INNER_HALF = 0.09;  // half the vessel's inner width (panel widths), either side of the aim: where stems may end
  var MEDIUM_MIN_GAP = 0.07;  // no untuned medium head closer than this to another head (panel widths; about the ring's own spacing)
  var STEM_REACH = 0.97;     // a head may stand at most this share of its stem from the aim
  var CLASS_BIAS = { L: -4, M: 0, S: 10 };  // draw-order layers (dz) per class: larges behind, smalls in front
  var CELL_KEY_RE = /^L([0-3])S([0-2])$/;

  // Where an untuned large / small STARTS, by order of appearance within its class.
  var DEFAULT_CLASS_ROWS = {
    L: [ { x: 0.585, y: 0.40 }, { x: 0.53, y: 0.42 }, { x: 0.64, y: 0.42 }, { x: 0.48, y: 0.45 }, { x: 0.69, y: 0.45 } ],
    S: [ { x: 0.585, y: 0.55 }, { x: 0.51, y: 0.555 }, { x: 0.66, y: 0.555 }, { x: 0.44, y: 0.565 } ]
  };

  // BAKED DEFAULTS for the new tuning keys. tools/vase-migrate-tuning.js rewrites
  // everything between the two markers (classes from the proposal, the two cells seeded
  // from Matilda's tuned six-flower posters). Anything not listed is a medium.
  // BEGIN-BAKED-6PLUS
  var BAKED_VASE_CLASSES = {
    "august": "L",
    "august__v2": "L",
    "july": "L",
    "january": "S",
    "january__v2": "S",
    "carnation": "S",
    "february": "S",
    "aster": "S",
    "april": "S",
    "may": "S",
    "december": "S"
  };
  var BAKED_CELLS = {
    "L1S2": { "m": [
      {"x": 0.612, "y": 0.253, "dz": 2},
      {"x": 0.642, "y": 0.406, "dz": -1},
      {"x": 0.7, "y": 0.58, "dz": -2}
    ] },
    "L2S1": { "m": [
      {"x": 0.516, "y": 0.325, "dz": 1},
      {"x": 0.66, "y": 0.7, "dz": -2},
      {"x": 0.79, "y": 0.35, "dz": -1}
    ] },
    "L0S0": { "m": [],
      "byCount": {
        "1": [
          {"x": 0.472, "y": 0.48400000000000004, "scale": 1.06, "facing": "left"}
        ],
        "2": [
          {"x": 0.508, "y": 0.49200000000000005, "facing": "left"},
          {"x": 0.642, "y": 0.4760000000000001, "facing": "right"}
        ],
        "3": [
          {"x": 0.528, "y": 0.416, "facing": "left"},
          {"x": 0.5179999999999999, "y": 0.5320000000000001},
          {"x": 0.6240000000000001, "y": 0.5219999999999999, "facing": "right"}
        ],
        "4": [
          {"x": 0.464, "y": 0.424},
          {"x": 0.61, "y": 0.392, "facing": "right"},
          {"x": 0.48000000000000004, "y": 0.4939999999999999},
          {"x": 0.626, "y": 0.542, "facing": "right"}
        ],
        "5": [
          {"x": 0.528, "y": 0.38799999999999996},
          {"x": 0.622, "y": 0.42400000000000004, "facing": "right"},
          {"x": 0.5200000000000001, "y": 0.55},
          {"x": 0.546, "y": 0.5419999999999999},
          {"x": 0.66, "y": 0.544, "facing": "right"}
        ]
      } }
  };
  // END-BAKED-6PLUS

  function cloneJson(o) { return JSON.parse(JSON.stringify(o)); }

  var VASE_CLASSES = cloneJson(BAKED_VASE_CLASSES);  // paintingKey -> "S" | "M" | "L" (M only to override a baked mark)
  var CLASS_ROWS = {};      // class -> FULL row, only for classes Matilda moved a slot of
  var CELLS = cloneJson(BAKED_CELLS);                // cellKey -> { m: [ {x, y[, dz]} ] }
  var CELL_NUDGES = {};     // cellKey -> paintingKey -> { dx, dy, rot, scale } (only non-neutral fields)
  var AIM = null;           // { x, y } once moved; null = the default under the vase opening
  var STEM_CUT = null;      // panel-y fraction below which no flower is drawn (with a vase); null = the default (effectiveStemCut)
  var STEM_ENDS = {};       // paintingKey -> { x, y, aspect }: injected (tests); measured ones come per render
  var LAST_CELL_COUNT = {}; // cellKey -> how many mediums it was last laid out with
  var LAST_PER_COUNT = {};  // cellKey -> { medium count: true when that layout stood on a per-count list (1-5 people) }

  function roundTo(v, d) { var m = Math.pow(10, d); return Math.round(v * m) / m; }
  function finite(v) { return typeof v === "number" && isFinite(v); }
  function clampRange(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function cloneSlot(s) { return s ? Object.assign({}, s) : s; }
  function ringSlot(x, y, dz) {
    var s = { x: roundTo(x, 4), y: roundTo(y, 4) };
    if (dz) s.dz = dz;
    return s;
  }

  function getVaseClass(paintingKey) {
    var c = VASE_CLASSES[canonKey(paintingKey)];
    return SIZE_CLASSES[c] ? c : "M";
  }

  /** cls null / "M" = unmarked. A baked S or L mark is overridden with an explicit "M" so the
   *  choice survives a reload (a reload starts from the baked marks again). */
  function setVaseClass(paintingKey, cls) {
    var k = canonKey(paintingKey);
    if (!k) return "M";
    var c = SIZE_CLASSES[cls] ? cls : "M";
    var baked = BAKED_VASE_CLASSES[k];
    if (c === "M" && !(baked && baked !== "M")) delete VASE_CLASSES[k];
    else VASE_CLASSES[k] = c;
    return c;
  }

  function parseCellKey(cellKey) {
    var m = CELL_KEY_RE.exec(String(cellKey || ""));
    return m ? { L: parseInt(m[1], 10), S: parseInt(m[2], 10) } : null;
  }

  /** The cell a mix of DRAWN paintings belongs to: larges capped at 3, smalls at 2.
   *  Takes entries ({ key, flowerKey }) or plain painting keys. */
  // L and S count DIFFERENT flowers, not copies (user 2026-10-23): July + July is L1, July + August
  // L2, so a second copy of a large or small never changes the mediums' layout. The copies stand on
  // their own spots (july flower1, flower2 ...) as before. A repeat painting (august__v2) is the same
  // flower; the month's other birth flower (__b) is a different one.
  function cellKeyFor(drawnEntries) {
    var seenL = {}, seenS = {}, nL = 0, nS = 0;
    (drawnEntries || []).forEach(function (e) {
      if (!e) return;
      var k = typeof e === "string" ? e : (e.flowerKey || e.key);
      var c = getVaseClass(k), u = classKeyOfFlower(k);
      if (c === "L" && !seenL[u]) { seenL[u] = true; nL++; }
      else if (c === "S" && !seenS[u]) { seenS[u] = true; nS++; }
    });
    return "L" + Math.min(nL, 3) + "S" + Math.min(nS, 2);
  }
  function classKeyOfFlower(k) { return canonKey(k).replace(/__v\d+$/, ""); }

  // ---- Class rows: where an untuned large / small starts ----
  function rowExtension(cls, i) {
    var row = DEFAULT_CLASS_ROWS[cls], last = row[row.length - 1];
    var k = i - row.length, ring = Math.floor(k / 2) + 1, side = (k % 2) ? -1 : 1;
    var step = cls === "S" ? 0.07 : 0.05;
    var off = Math.abs(last.x - BOUQUET_CX) + step * ring;
    return { x: roundTo(clampRange(BOUQUET_CX + side * off, 0.08, 0.92), 4), y: roundTo(last.y + (cls === "S" ? 0.01 : 0.02) * ring, 4) };
  }
  function rowDefault(cls, i) {
    var row = DEFAULT_CLASS_ROWS[cls];
    return i < row.length ? cloneSlot(row[i]) : rowExtension(cls, i);
  }
  function getClassRow(cls, i) {
    cls = cls === "S" ? "S" : "L";
    i = Math.max(0, i | 0);
    var r = CLASS_ROWS[cls];
    return cloneSlot((r && r[i]) || rowDefault(cls, i));
  }
  function setClassRow(cls, i, pos) {
    if (cls !== "S" && cls !== "L") return null;
    i = i | 0;
    if (i < 0 || !pos) return null;
    var row = CLASS_ROWS[cls];
    if (!row) {
      row = CLASS_ROWS[cls] = [];
      DEFAULT_CLASS_ROWS[cls].forEach(function (p) { row.push(cloneSlot(p)); });
    }
    while (row.length <= i) row.push(rowDefault(cls, row.length));
    var prev = row[i];
    row[i] = { x: finite(pos.x) ? clampRange(pos.x, -0.5, 1.5) : prev.x, y: finite(pos.y) ? clampRange(pos.y, -0.5, 1.5) : prev.y };
    return cloneSlot(row[i]);
  }

  // ---- The ring rule: where mediums go in a cell nobody has tuned ----
  // A ring around the larges, centred on the bouquet. Radius grows with the larges, the gap
  // at the bottom grows with the smalls (they stand there), and the whole ring lifts a hair
  // for each small. Slots come back in FILL order: centre first when there is one, then
  // left/right pairs outward; a second ring starts once the first is full. With no larges
  // the first medium takes the centre itself. Count-dependent on purpose (a ring of 7 is
  // spaced differently from a ring of 5), so a cell's tuned slots win and only the missing
  // ones come from here.
  function ringSlots(L, S, M) { return ringLayout(L, S, M).slots; }

  // The same layout plus the PAIR STRUCTURE: partner[i] = the slot that mirrors slot i, or -1
  // for a single (the centre one). It is a property of the slot ORDER, not of where the slots
  // currently stand, so moving one slot of a pair never un-pairs it. In fill order a ring
  // lists its centre slot first (when it has one), then left/right pairs: (1st, 2nd), (3rd, 4th)...
  // The second of a pair is the "mirrored" one.
  function ringLayout(L, S, M) {
    L = clampRange(L | 0, 0, 3); S = clampRange(S | 0, 0, 2); M = Math.max(0, M | 0);
    var out = [], partner = [];
    if (!M) return { slots: out, partner: partner };
    var yc = BOUQUET_CY - 0.012 * S;
    var rad = Math.PI / 180;
    var ringM = M;
    // No larges: the first medium takes the centre, except when there are only two: they stand
    // side by side across the centre (a core and a top slot share one line to the aim).
    if (L === 0 && M !== 2) { out.push(ringSlot(BOUQUET_CX, yc, 0)); partner.push(-1); ringM = M - 1; }
    var maxA = 180 - [55, 70, 85][S];                 // the arc the ring may use, from straight up
    var r = 0.15 + 0.035 * L + (L === 0 ? -0.02 : 0);
    var cap = Math.max(2, Math.floor((2 * maxA) / 38) + 1);
    var left = ringM, ri = 0;
    while (left > 0) {
      var n = Math.min(cap, left); left -= n;
      var step = Math.min(42, (2 * maxA) / Math.max(1, n));
      var rr = r + ri * 0.06;
      var odd = n % 2 === 1, pairs = Math.floor(n / 2);
      if (odd) {
        // With larges the odd one stands in front, so it never hides the tall spike; with
        // none it goes on top of the centre medium.
        if (L > 0) out.push(ringSlot(BOUQUET_CX, yc + 0.075 + ri * 0.03, 6));
        else out.push(ringSlot(BOUQUET_CX, yc - rr / PANEL_AR, ri * 2));
        partner.push(-1);
      }
      // Pairs hug the larges' flanks: the fan is rotated outward by half the spare arc.
      var off = L > 0 ? Math.min(30, maxA - (pairs - 0.5) * step) * 0.5 : 0;
      for (var i = 0; i < pairs; i++) {
        var a0 = ((odd && L === 0) ? i + 1 : i + 0.5) * step;
        [-a0, a0].forEach(function (s) {
          var a = rad * (Math.sign(s) * (Math.abs(s) + off));
          out.push(ringSlot(BOUQUET_CX + rr * Math.sin(a), yc - rr * Math.cos(a) / PANEL_AR, ri * 2 + (Math.abs(s) > 90 ? 2 : 0)));
        });
        partner.push(out.length - 1, out.length - 2);   // the left slot's partner is the right one, and back
      }
      ri++;
    }
    return { slots: out, partner: partner };
  }

  /** Which of a cell's `count` medium slots are partners: partner[i] is the slot that mirrors
   *  slot i, or -1. Follows the slot ORDER (see ringLayout), whatever the slots have been tuned to. */
  function cellPairs(cellKey, count) {
    var pc = parseCellKey(cellKey);
    return pc ? ringLayout(pc.L, pc.S, count).partner : [];
  }

  // ---- Per-count medium slots (1-5 mediums) ----
  // A cell can also hold a slot list per medium COUNT, for 1 to 5 mediums: cells.L0S0.byCount["3"].
  // That is where Matilda's hand-composed 1-5 layouts live now (p1c0, p2c0 ... p2c3, seeded by
  // tools/vase-migrate-tuning.js): three mediums in L0S0 take her three p2c1 positions, in her
  // order, instead of the ring. A count with no list of its own uses `m` / the ring as before.
  // Besides x, y and dz such a slot may carry `scale` (multiplies the size of whatever stands
  // there) and `facing` (which way that position wants its flower to face), like her old slots.
  // A mix keeps its own medium layout per number of MEDIUMS, at any number of people (user
  // 2026-10-22: "the only difference at 6+ is the pair flowers"). 20: no practical cap (a 14-person
  // calibration poster has at most 14 mediums).
  var MAX_COUNT_SLOTS = 20;
  function normCellSlot(p) {
    if (!p || !finite(p.x) || !finite(p.y)) return null;
    var s = { x: p.x, y: p.y };
    var dz = finite(p.dz) ? Math.round(clampRange(p.dz, -20, 20)) : 0;
    if (dz) s.dz = dz;
    if (finite(p.scale) && p.scale > 0 && p.scale !== 1) s.scale = clampRange(p.scale, 0.3, 3);
    if (p.facing === "left" || p.facing === "right") s.facing = p.facing;
    return s;
  }
  function countSlotsOf(cellKey, count) {
    var c = CELLS[cellKey], bc = c && c.byCount;
    count = count | 0;
    return (bc && count >= 1 && count <= MAX_COUNT_SLOTS && Array.isArray(bc[count])) ? bc[count] : null;
  }
  /** True when the cell has its own slot list for exactly `count` mediums (1-5). */
  function hasCountSlots(cellKey, count) { return !!countSlotsOf(cellKey, count); }
  // Per-count data is read and written for ANY poster that has a list for its number of mediums
  // (2026-10-22; until then only at 1-5 people, so L1S0 with 4 mediums and L2S0 with 4 mediums,
  // 5 vs 6 people, could never share a layout). `people` is accepted and no longer decides.
  function perCountPoster(cellKey, count, people) {
    count = count | 0;
    return count >= 1 && count <= MAX_COUNT_SLOTS;
  }

  /** The medium slots a cell offers `count` mediums: its per-count list when it has one for that
   *  count, else its tuned slots; the ring rule for the rest. */
  function getCellSlots(cellKey, count, people) {
    var pc = parseCellKey(cellKey);
    count = Math.max(0, count | 0);
    if (!pc || !count) return [];
    var own = countSlotsOf(cellKey, count);
    var tuned = own || (CELLS[cellKey] && CELLS[cellKey].m) || [];
    var out = tuned.slice(0, count).map(cloneSlot);
    if (out.length < count) {
      var ring = ringSlots(pc.L, pc.S, count);
      for (var i = out.length; i < count; i++) out.push(ring[i]);
    }
    return out;
  }

  /** Store slot i of a cell: in its per-count list when it has one for `count` (1-5), else in
   *  `m`. Slots before it that were never tuned are written down first, from the ring rule at
   *  the count the cell was last drawn with, so nothing else moves. */
  function setCellSlot(cellKey, i, pos, count, people) {
    var pc = parseCellKey(cellKey);
    i = i | 0;
    if (!pc || i < 0 || !pos) return null;
    var c = CELLS[cellKey] || (CELLS[cellKey] = { m: [] });
    var list = (perCountPoster(cellKey, count, people) && countSlotsOf(cellKey, count)) || c.m;
    if (list.length <= i) {
      var ring = ringSlots(pc.L, pc.S, Math.max(count | 0, LAST_CELL_COUNT[cellKey] | 0, i + 1));
      for (var j = list.length; j <= i; j++) list.push(cloneSlot(ring[j]));
    }
    var prev = list[i];
    var s = { x: finite(pos.x) ? clampRange(pos.x, -0.5, 1.5) : prev.x, y: finite(pos.y) ? clampRange(pos.y, -0.5, 1.5) : prev.y };
    var dz = finite(pos.dz) ? Math.round(clampRange(pos.dz, -20, 20)) : (prev.dz || 0);
    if (dz) s.dz = dz;
    if (prev.scale) s.scale = prev.scale;      // a moved slot keeps its size and facing
    if (prev.facing) s.facing = prev.facing;
    // pos.facing "left"/"right" sets the direction of this position, null takes it away.
    if (pos.facing === "left" || pos.facing === "right") s.facing = pos.facing;
    else if (pos.facing === null) delete s.facing;
    list[i] = s;
    return cloneSlot(s);
  }

  /** Copy one cell's medium slots (`m` and the per-count lists) into another. Returns what the
   *  target held before (a cell object, or null when it had none) for restoreCell(); undefined
   *  when nothing was copied. The cells are independent afterwards. A source nobody tuned
   *  copies its ring slots. */
  function copyCell(fromKey, toKey) {
    var a = parseCellKey(fromKey), b = parseCellKey(toKey);
    if (!a || !b || fromKey === toKey) return undefined;
    var prev = CELLS[toKey] ? cloneJson(CELLS[toKey]) : null;
    var src = CELLS[fromKey];
    var m = (src && src.m && src.m.length)
      ? src.m.map(cloneSlot)
      : ringSlots(a.L, a.S, LAST_CELL_COUNT[fromKey] || LAST_CELL_COUNT[toKey] || 6);
    CELLS[toKey] = { m: m };
    if (src && src.byCount && Object.keys(src.byCount).length) CELLS[toKey].byCount = cloneJson(src.byCount);
    if (prev && prev.L) CELLS[toKey].L = cloneJson(prev.L);   // the target's own large positions stay
    return prev;
  }
  /** How many medium slots each tuned cell holds: { cellKey: count }. A fresh object, cheap to
   *  call on every render (the workbench used to read getAdjustmentsJson for this). */
  function getTunedCellCounts() {
    var out = {};
    Object.keys(CELLS).forEach(function (k) { out[k] = (CELLS[k].m || []).length; });
    return out;
  }
  /** The baked size classes (the proposal, or what the migration last wrote): the one list
   *  tools/vase-migrate-tuning.js starts from. */
  function getBakedVaseClasses() { return cloneJson(BAKED_VASE_CLASSES); }
  /** Put back what copyCell returned: null removes the cell, a cell object restores it,
   *  undefined (a copy that never happened) does nothing. */
  function restoreCell(cellKey, prev) {
    if (!parseCellKey(cellKey) || prev === undefined) return false;
    if (prev === null) delete CELLS[cellKey]; else CELLS[cellKey] = cloneJson(prev);
    return true;
  }

  // ---- Global large positions (L1, L2, L3) per mix ----
  // cells.<LxSy>.L = { "1": { x, y, dz?, facing?, scale? }, ... }: where the Nth large painting of a
  // poster in this mix stands, whatever painting it is, at any number of mediums (L static). The
  // Nth large = the Nth different large painting in poster order; only its first copy stands here
  // (later copies keep their own spots). A mix without an L slot keeps the painting's own spot.
  var MAX_L_SLOTS = 3;
  function getCellLSlot(cellKey, k) {
    var c = CELLS[cellKey], L = c && c.L;
    k = k | 0;
    return (L && k >= 1 && k <= MAX_L_SLOTS && L[k]) ? cloneSlot(L[k]) : null;
  }
  /** patch merges onto the L slot (x, y, dz; facing "left"/"right", null clears it); null removes
   *  the slot. Returns the slot now in force (or null). */
  function setCellLSlot(cellKey, k, patch) {
    k = k | 0;
    if (!parseCellKey(cellKey) || k < 1 || k > MAX_L_SLOTS) return null;
    var c = CELLS[cellKey] || (CELLS[cellKey] = { m: [] });
    var L = c.L || (c.L = {});
    if (patch === null) { delete L[k]; if (!Object.keys(L).length) delete c.L; return null; }
    var prev = L[k] || {};
    var nx = finite(patch.x) ? patch.x : prev.x, ny = finite(patch.y) ? patch.y : prev.y;
    if (!finite(nx) || !finite(ny)) return null;
    var next = { x: clampRange(nx, -0.5, 1.5), y: clampRange(ny, -0.5, 1.5) };
    var dz = finite(patch.dz) ? patch.dz : prev.dz;
    if (finite(dz) && Math.round(dz)) next.dz = Math.round(clampRange(dz, -20, 20));
    if (prev.scale) next.scale = prev.scale;
    var fc = patch.facing === undefined ? prev.facing : patch.facing;
    if (fc === "left" || fc === "right") next.facing = fc;
    L[k] = next;
    return cloneSlot(next);
  }
  // Until a mix has an L1 of its own, L1 stands where July's first copy stands (its flower1 spot:
  // x, y and layer; user 2026-10-23 "use the July 1 positions as base for L1"). Size, angle and
  // direction stay each painting's own.
  var L1_BASE_PAINTING = "july";
  function getCellLBase(cellKey, k) {
    var own = getCellLSlot(cellKey, k);
    if (own || (k | 0) !== 1) return own;
    var j = speciesSpot(L1_BASE_PAINTING, 1);
    if (!j || !finite(j.x) || !finite(j.y)) return null;
    var b = { x: j.x, y: j.y };
    if (finite(j.dz) && j.dz) b.dz = j.dz;
    return b;
  }
  function lNudgeKey(paintingKey, k) { return canonKey(paintingKey) + "@" + (k | 0); }
  /** Flower unique on L#: this painting when it stands on L# of this mix. */
  function getCellLNudge(cellKey, paintingKey, k) {
    var byCell = CELL_NUDGES[cellKey], L = byCell && byCell.L;
    return Object.assign({}, NEUTRAL_NUDGE, (L && L[lNudgeKey(paintingKey, k)]) || {});
  }
  function setCellLNudge(cellKey, paintingKey, k, patch) {
    if (!parseCellKey(cellKey) || !canonKey(paintingKey) || (k | 0) < 1) return null;
    var byCell = CELL_NUDGES[cellKey] || (CELL_NUDGES[cellKey] = {});
    var L = byCell.L || (byCell.L = {}), key = lNudgeKey(paintingKey, k);
    var next = patch === null ? {} : normNudge(Object.assign({}, NEUTRAL_NUDGE, L[key] || {}, patch || {}));
    if (Object.keys(next).length) L[key] = next; else delete L[key];
    if (!Object.keys(L).length) delete byCell.L;
    if (!Object.keys(byCell).length) delete CELL_NUDGES[cellKey];
    return getCellLNudge(cellKey, paintingKey, k);
  }

  // ---- Per-painting nudge inside one cell ----
  // Only the fields that differ from neutral are stored, so the JSON stays small and a
  // reload gives back exactly what was saved.
  function normNudge(n) {
    var out = {};
    if (!n || typeof n !== "object") return out;
    if (finite(n.dx) && n.dx) out.dx = clampRange(n.dx, -0.3, 0.3);
    if (finite(n.dy) && n.dy) out.dy = clampRange(n.dy, -0.3, 0.3);
    if (finite(n.rot) && n.rot) out.rot = clampRange(n.rot, -1.5, 1.5);
    if (finite(n.scale) && n.scale > 0 && n.scale !== 1) out.scale = clampRange(n.scale, 0.3, 3);
    // A medium's facing and layer live here too: its old "flowerN" facing/dz is not read at 6+.
    if (n.facing === "left" || n.facing === "right") out.facing = n.facing;
    if (finite(n.dz)) { var dz = Math.round(clampRange(n.dz, -20, 20)); if (dz) out.dz = dz; }
    return out;
  }
  var NEUTRAL_NUDGE = { dx: 0, dy: 0, rot: 0, scale: 1, dz: 0, facing: null };
  // Per-count nudges (1-5 mediums): cellNudges.L0S0.byCount["5"] = { "june": {...}, "june@3": {...} }.
  // "june@3" is june when it stands in slot 3 of that count's list; it wins over "june", which
  // wins over the cell-wide nudge. Matilda's 1-5 offsets were per POSITION, so the migration
  // writes them as "<painting>@<slot>".
  var NUDGE_KEY_RE = /^(.+)@(\d+)$/;
  function nudgeKey(paintingKey, slot) {
    var k = canonKey(paintingKey);
    return (k && finite(slot) && slot >= 0) ? k + "@" + (slot | 0) : k;
  }
  function countNudgesOf(cellKey, count) {
    var byCell = CELL_NUDGES[cellKey], bc = byCell && byCell.byCount;
    count = count | 0;
    return (bc && count >= 1 && count <= MAX_COUNT_SLOTS && bc[count] && typeof bc[count] === "object") ? bc[count] : null;
  }
  // The stored nudge in force (not merged with neutral), or null.
  function storedNudge(cellKey, paintingKey, count, slot) {
    var k = canonKey(paintingKey), list = countNudgesOf(cellKey, count);
    if (list && finite(slot) && list[nudgeKey(k, slot)]) return list[nudgeKey(k, slot)];
    if (list && list[k]) return list[k];
    var byCell = CELL_NUDGES[cellKey];
    return (byCell && byCell[k]) || null;
  }
  /** count (1-5) and slot are optional: with them, a per-count nudge for that painting (in that
   *  slot) is read when there is one, else the cell-wide one. */
  function getCellNudge(cellKey, paintingKey, count, slot) {
    return Object.assign({}, NEUTRAL_NUDGE, storedNudge(cellKey, paintingKey, count, slot) || {});
  }
  /** patch merges onto the nudge in force, null removes the stored one. With a `count` (1-5) of
   *  a 1-5 people poster whose cell has its own slots for that count, or a nudge list for it, the
   *  nudge goes to that count's list, created if needed ("<painting>@<slot>" when a slot is given):
   *  a "this painting only" nudge on a 3-flower poster must not move the painting in every other
   *  poster of the cell. Otherwise cell-wide. Returns the nudge now in force. */
  function setCellNudge(cellKey, paintingKey, patch, count, slot, people) {
    var k = canonKey(paintingKey);
    if (!parseCellKey(cellKey) || !k) return null;
    var byCell = CELL_NUDGES[cellKey] || (CELL_NUDGES[cellKey] = {});
    var list = null;
    if (perCountPoster(cellKey, count, people) && (hasCountSlots(cellKey, count) || countNudgesOf(cellKey, count))) {
      var bcs = byCell.byCount = byCell.byCount || {};
      list = bcs[count | 0] = bcs[count | 0] || {};
    }
    var target = list || byCell, tk = list ? nudgeKey(k, slot) : k;
    var cnt = list ? count : undefined;
    var cur = patch === null ? null : (target[tk] || storedNudge(cellKey, k, cnt, slot));
    var next = patch === null ? {} : normNudge(Object.assign({}, NEUTRAL_NUDGE, cur || {}, patch || {}));
    if (Object.keys(next).length) target[tk] = next; else delete target[tk];
    if (!Object.keys(byCell).length) delete CELL_NUDGES[cellKey];
    return getCellNudge(cellKey, k, cnt, slot);
  }

  // ---- The aim every stem points at ----
  function getAim() { return Object.assign({}, AIM || DEFAULT_AIM); }
  function setAim(a) {
    if (a === null) { AIM = null; return getAim(); }
    if (!a || !finite(a.x) || !finite(a.y)) return null;
    AIM = { x: clampRange(a.x, -0.5, 1.5), y: clampRange(a.y, -0.5, 1.5) };
    return getAim();
  }
  // ---- The stem cut: no flower (stem, leaves, shadow) is drawn below this line when a vase is drawn ----
  /** The tuned cut line (panel-y fraction), or null when unset (the default applies). */
  function getStemCut() { return STEM_CUT; }
  function setStemCut(v) {
    STEM_CUT = (v === null || v === undefined || !finite(Number(v))) ? null : clampRange(Number(v), 0.3, 1);
    return STEM_CUT;
  }
  /** The cut line actually used for this vase (panel-y fraction): the tuned one, else the default =
   *  the vase floor (vaseFloorFor) or the painted vessel's bottom when that is higher (minus a hair),
   *  so nothing shows under or beside the vase base. Null without a drawn vase. */
  function effectiveStemCut(vase, imagesByKey, artboard) {
    var floor = vaseFloorFor(vase, imagesByKey, artboard);
    if (!finite(floor)) return null;
    if (finite(STEM_CUT)) return STEM_CUT;
    var img = imagesByKey[vase.key];
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    var wf = Number(vase.width); if (!isFinite(wf) || wf <= 0) wf = 0.3;
    var my = Number(vase.mouthY); if (!isFinite(my)) my = 0.62;
    var iax = Number(vase.imgMouthX); if (!isFinite(iax)) iax = 0.5;
    var iay = Number(vase.imgMouthY); if (!isFinite(iay)) iay = 0;
    my = Math.min(1, Math.max(0, my)); iax = Math.min(1, Math.max(0, iax)); iay = Math.min(1, Math.max(0, iay));
    var vhF = artboard.w * wf * (ih / iw) / artboard.h;   // drawn vase height, share of the panel height
    var vb = vesselBottomFrac(img, iax);
    return finite(vb) ? Math.min(floor, my + (vb - iay - 0.02) * vhF) : floor;
  }

  /** Where the stem cut applies across the panel: the drawn VESSEL's width (vesselSidesFrac, not the
   *  image with its shadow; the whole image when it cannot be read) plus STEM_CUT_SIDE of that
   *  width on each side, as panel-x fractions { l, r } (same placement as the vase drawing in
   *  renderPoster). Beside it nothing is cut, so petals lying on the table show. Null without a vase. */
  var STEM_CUT_SIDE = 0.06;
  function stemCutBand(vase, imagesByKey, artboard) {
    var img = vase && vase.key && imagesByKey && imagesByKey[vase.key];
    var iw = img && (img.naturalWidth || img.width);
    if (!iw || !artboard || !artboard.w) return null;
    var wf = Number(vase.width); if (!isFinite(wf) || wf <= 0) wf = 0.3;
    var mx = Number(vase.mouthX); if (!isFinite(mx)) mx = 0.5;
    var iax = Number(vase.imgMouthX); if (!isFinite(iax)) iax = 0.5;
    iax = Math.min(1, Math.max(0, iax));
    var l = mx - wf * iax, r = l + wf;
    var vs = vesselSidesFrac(img);
    if (vs) { r = l + wf * vs.r; l = l + wf * vs.l; }
    var pad = (r - l) * STEM_CUT_SIDE;
    return { l: l - pad, r: r + pad };
  }

  /** The aim when Matilda has not moved it: the validated default, shifted with the vase if
   *  the placement it was drawn for has since moved. A zoomed vase (scaledVase) keeps the aim of
   *  its unzoomed placement, so a vase's size never moves the flowers. */
  function defaultAimFor(vase) {
    var a = { x: DEFAULT_AIM.x, y: DEFAULT_AIM.y };
    var mx = vase && (finite(vase.aimMouthX) ? vase.aimMouthX : vase.mouthX);
    var my = vase && (finite(vase.aimMouthY) ? vase.aimMouthY : vase.mouthY);
    if (finite(mx) && finite(my)) {
      a.x += mx - REFERENCE_VASE_MOUTH.x;
      a.y += my - REFERENCE_VASE_MOUTH.y;
    }
    return a;
  }

  /** The vase drawn at its own zoom (vase.scale, 1 = the shared placement): wider by that factor,
   *  growing around the bottom middle of the painted vessel, so it keeps standing on the table.
   *  Returns a copy with width / mouthX / mouthY changed and scale 1; the unzoomed mouth is kept
   *  as aimMouthX/Y for defaultAimFor. Without a readable image the anchor is the image's
   *  mouth column at its bottom edge. */
  function scaledVase(vase, imagesByKey, artboard) {
    var s = Number(vase && vase.scale);
    if (!vase || !finite(s) || s <= 0 || Math.abs(s - 1) < 1e-6) return vase;
    var img = vase.key && imagesByKey && imagesByKey[vase.key];
    var iw = img && (img.naturalWidth || img.width), ih = img && (img.naturalHeight || img.height);
    if (!iw || !ih || !artboard || !artboard.w || !artboard.h) return vase;
    var wf = Number(vase.width); if (!isFinite(wf) || wf <= 0) wf = 0.3;
    var mx = Number(vase.mouthX); if (!isFinite(mx)) mx = 0.5;
    var my = Number(vase.mouthY); if (!isFinite(my)) my = 0.62;
    var iax = Number(vase.imgMouthX); if (!isFinite(iax)) iax = 0.5;
    var iay = Number(vase.imgMouthY); if (!isFinite(iay)) iay = 0;
    // The fixed point, in image fractions: the middle of the vessel, on the line where it stands.
    var sides = vesselSidesFrac(img);
    var fx = sides ? (sides.l + sides.r) / 2 : iax;
    var fy = sides && finite(sides.base) ? sides.base : 1;
    var hF = artboard.w * wf * (ih / iw) / artboard.h;      // drawn vase height, share of the panel height
    return Object.assign({}, vase, {
      width: wf * s,
      mouthX: mx - (s - 1) * wf * (fx - iax),
      mouthY: my - (s - 1) * hF * (fy - iay),
      scale: 1,
      aimMouthX: finite(vase.aimMouthX) ? vase.aimMouthX : mx,
      aimMouthY: finite(vase.aimMouthY) ? vase.aimMouthY : my
    });
  }

  // ---- Stem ends ----
  // Where a painting's stem ENDS, as a fraction of its image: the tip of the stem that grows from
  // the flower (see stemTipFromAlpha). Measured once per image (cached by src and anchor) when
  // renderPoster has the images; setStemEnd() injects one for Node tests. aspect = width / height.
  function setStemEnd(paintingKey, se) {
    var k = canonKey(paintingKey);
    if (!k) return null;
    if (se === null) { delete STEM_ENDS[k]; return null; }
    if (!se || !finite(se.x) || !finite(se.y)) return null;
    STEM_ENDS[k] = { x: clampRange(se.x, 0, 1), y: clampRange(se.y, 0, 1), aspect: (finite(se.aspect) && se.aspect > 0) ? se.aspect : 1 };
    return Object.assign({}, STEM_ENDS[k]);
  }
  // The stem's tip in an RGBA pixel buffer (w x h), as a fraction of the image, or null.
  // NOT "the lowest opaque row": paintings carry loose bits (februari has two fallen petals in its
  // bottom corner, under the flower's far side) and the real stem tip is a faint 1-2 px line
  // (alpha ~10-30). So: every pixel with alpha > STEM_ALPHA counts, single ones too; take the
  // piece that is CONNECTED to the flower (grown from the pixel nearest the anchor, with gaps of up
  // to two pixels bridged, so a faint stem that fades out for a pixel stays one piece); in it,
  // the tip is where it reaches farthest from the anchor below the anchor (averaged over the
  // last 1.5 px, so a blunt end gives its middle).
  var STEM_ALPHA = 8;
  function stemTipFromAlpha(d, w, h, anchor) {
    if (!d || !(w > 0) || !(h > 0)) return null;
    var ax = (anchor && finite(anchor.x) ? anchor.x : 0.5) * w, ay = (anchor && finite(anchor.y) ? anchor.y : 0.5) * h;
    var on = function (i) { return d[i * 4 + 3] > STEM_ALPHA; };
    var seed = -1, sd = Infinity, i, xx, yy;
    for (i = 0; i < w * h; i++) {
      if (!on(i)) continue;
      xx = i % w; yy = (i - xx) / w;
      var dd = (xx - ax) * (xx - ax) + (yy - ay) * (yy - ay);
      if (dd < sd) { sd = dd; seed = i; }
    }
    if (seed < 0) return null;
    var seen = new Uint8Array(w * h);
    function grow(from) {
      var stack = [from], out = [];
      seen[from] = 1;
      while (stack.length) {
        var p = stack.pop(), px = p % w, py = (p - px) / w;
        out.push(p);
        for (var oy = -3; oy <= 3; oy++) for (var ox = -3; ox <= 3; ox++) {   // bridges gaps of up to 2 px
          var nx = px + ox, ny = py + oy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          var q = ny * w + nx;
          if (!seen[q] && on(q)) { seen[q] = 1; stack.push(q); }
        }
      }
      return out;
    }
    // The piece at the anchor, unless that is a stray bit (under a fifth of the painting): then the
    // biggest piece, which is the flower with its stem.
    var comp = grow(seed), total = comp.length, biggest = comp;
    for (i = 0; i < w * h; i++) {
      if (seen[i] || !on(i)) continue;
      var c2 = grow(i); total += c2.length;
      if (c2.length > biggest.length) biggest = c2;
    }
    if (comp.length < 0.2 * total) comp = biggest;
    var best = -1, dist = new Float64Array(comp.length);
    comp.forEach(function (p, k) {
      var x0 = p % w, y0 = (p - x0) / w;
      dist[k] = y0 + 0.5 > ay ? Math.hypot(x0 + 0.5 - ax, y0 + 0.5 - ay) : -1;
      if (dist[k] > best) best = dist[k];
    });
    if (best < 0) return null;
    var sx = 0, sy = 0, n = 0;
    comp.forEach(function (p, k) {
      if (dist[k] < best - 1.5) return;
      var x0 = p % w; sx += x0 + 0.5; sy += (p - x0) / w + 0.5; n++;
    });
    return { x: sx / n / w, y: sy / n / h };
  }
  var STEM_END_CACHE = {};
  function measureStemEnd(img, anchor) {
    var iw = img && (img.naturalWidth || img.width), ih = img && (img.naturalHeight || img.height);
    if (!iw || !ih || typeof document === "undefined") return null;
    var src = String(img.currentSrc || img.src || "");
    var ax = anchor && finite(anchor.x) ? anchor.x : 0.5, ay = anchor && finite(anchor.y) ? anchor.y : 0.5;
    var ck = src ? (src + "@" + iw + "x" + ih + "@" + ax + "," + ay) : "";
    if (ck && Object.prototype.hasOwnProperty.call(STEM_END_CACHE, ck)) return STEM_END_CACHE[ck];
    var res = null;
    try {
      var m = 400 / Math.max(iw, ih), w = Math.max(1, Math.round(iw * m)), h = Math.max(1, Math.round(ih * m));
      var c = document.createElement("canvas"); c.width = w; c.height = h;
      var x = c.getContext("2d"); x.drawImage(img, 0, 0, w, h);
      var tip = stemTipFromAlpha(x.getImageData(0, 0, w, h).data, w, h, { x: ax, y: ay });
      if (tip) res = { x: tip.x, y: tip.y, aspect: iw / ih };
    } catch (_) { res = null; }   // a tainted canvas cannot be read: no automatic angle for it
    if (ck) STEM_END_CACHE[ck] = res;
    return res;
  }

  // Where the painted VESSEL ends, as a share of the vase image height: the highest of the lowest
  // opaque rows in a few columns around the mouth (the bottom is rounded, and the cast shadow lies
  // to one side, not under the middle). Used to clip the flowers above it, so a stem longer than
  // the vessel never shows under the vase. Null without a canvas (Node) or a readable image.
  var VESSEL_BOTTOM_CACHE = {};
  function vesselBottomFrac(img, mouthX) {
    var iw = img && (img.naturalWidth || img.width), ih = img && (img.naturalHeight || img.height);
    if (!iw || !ih || typeof document === "undefined") return null;
    var src = String(img.currentSrc || img.src || ""), ck = src ? (src + "@" + iw + "x" + ih + "@" + mouthX) : "";
    if (ck && Object.prototype.hasOwnProperty.call(VESSEL_BOTTOM_CACHE, ck)) return VESSEL_BOTTOM_CACHE[ck];
    var res = null;
    try {
      var m = 400 / Math.max(iw, ih), w = Math.max(1, Math.round(iw * m)), h = Math.max(1, Math.round(ih * m));
      var c = document.createElement("canvas"); c.width = w; c.height = h;
      var x = c.getContext("2d"); x.drawImage(img, 0, 0, w, h);
      var d = x.getImageData(0, 0, w, h).data;
      [-0.08, -0.04, 0, 0.04, 0.08].forEach(function (off) {
        var col = Math.round((mouthX + off) * w);
        if (col < 0 || col >= w) return;
        for (var y = h - 1; y >= 0; y--) {
          if (d[(y * w + col) * 4 + 3] > 128) { var fr = (y + 1) / h; if (res === null || fr < res) res = fr; return; }
        }
      });
    } catch (_) { res = null; }
    if (ck) VESSEL_BOTTOM_CACHE[ck] = res;
    return res;
  }

  // Where the painted VESSEL is left to right, as shares of the vase image width { l, r }: the
  // widest opaque extent over the rows above the cast shadow (top 75% of the image). The PNGs are
  // about twice as wide as the vase because the shadow runs out to the right edge (2026-10-25:
  // the stem cut hid petals on the table right of the vase). Null without a canvas or a readable image.
  var VESSEL_SIDES_CACHE = {};
  var VESSEL_SIDES_ROWS = 0.75;
  function vesselSidesFrac(img) {
    var iw = img && (img.naturalWidth || img.width), ih = img && (img.naturalHeight || img.height);
    if (!iw || !ih || typeof document === "undefined") return null;
    var src = String(img.currentSrc || img.src || ""), ck = src ? (src + "@" + iw + "x" + ih) : "";
    if (ck && Object.prototype.hasOwnProperty.call(VESSEL_SIDES_CACHE, ck)) return VESSEL_SIDES_CACHE[ck];
    var res = null;
    try {
      var m = 400 / Math.max(iw, ih), w = Math.max(1, Math.round(iw * m)), h = Math.max(1, Math.round(ih * m));
      var c = document.createElement("canvas"); c.width = w; c.height = h;
      var x = c.getContext("2d"); x.drawImage(img, 0, 0, w, h);
      var d = x.getImageData(0, 0, w, h).data, l = w, r = -1;
      for (var y = 0; y < Math.round(h * VESSEL_SIDES_ROWS); y++) {
        for (var a = 0; a < w; a++) if (d[(y * w + a) * 4 + 3] > 128) { if (a < l) l = a; break; }
        for (var b = w - 1; b >= 0; b--) if (d[(y * w + b) * 4 + 3] > 128) { if (b > r) r = b; break; }
      }
      if (r >= l) {
        // base = where the vessel stands: the first row below 60% whose right edge runs past the
        // body's (the cast shadow starts at the foot of the vase). No shadow found = the image bottom.
        var base = 1;
        for (var yb = Math.round(h * 0.6); yb < h; yb++) {
          var rb = -1;
          for (var bb = w - 1; bb >= 0; bb--) if (d[(yb * w + bb) * 4 + 3] > 128) { rb = bb; break; }
          if (rb > r + w * 0.04) { base = yb / h; break; }
        }
        res = { l: l / w, r: (r + 1) / w, base: base };
      }
    } catch (_) { res = null; }
    if (ck) VESSEL_SIDES_CACHE[ck] = res;
    return res;
  }

  // Pristine copies of the baked per-species maps, captured at load (before anything can
  // mutate them), so a full reset can restore the factory calibration exactly.
  var BAKED_SPECIES_OFFSETS = JSON.parse(JSON.stringify(SPECIES_OFFSETS));
  var BAKED_ANCHOR_OVERRIDES = JSON.parse(JSON.stringify(ANCHOR_OVERRIDES));
  var BAKED_SPECIES_SCALES = JSON.parse(JSON.stringify(SPECIES_SCALES));

  /**
   * Full reset to the factory built-ins: count baselines, split layouts, per-species
   * offsets/anchors/scales, and margins. Used when switching bouquet styles -- each
   * style's own "Position overrides (JSON)" is applied onto this clean slate, so
   * styles can carry completely different position sets without bleeding into each
   * other. The baked values ARE the "original" style's calibration.
   */
  function resetAllAdjustments() {
    WORKING = null; // lazily rebuilt from the pristine DEFAULT_TEMPLATES / DEFAULT_SPLITS
    SPECIES_OFFSETS = JSON.parse(JSON.stringify(BAKED_SPECIES_OFFSETS));
    ANCHOR_OVERRIDES = JSON.parse(JSON.stringify(BAKED_ANCHOR_OVERRIDES));
    SPECIES_SCALES = JSON.parse(JSON.stringify(BAKED_SPECIES_SCALES));
    SPECIES_ROTATIONS = {};
    SPECIES_FLIPS = {};
    SPECIES_DIRECTIONS = {};
    SLOT_FACINGS = {};
    MARGIN_OVERRIDES = {};
    // The 6+ keys restart from their baked marks and seeded cells; the rest is empty.
    VASE_CLASSES = cloneJson(BAKED_VASE_CLASSES);
    CLASS_ROWS = {};
    CELLS = cloneJson(BAKED_CELLS);
    CELL_NUDGES = {};
    AIM = null;
    STEM_CUT = null;
    LAST_PER_COUNT = {};
  }

  // PER-STYLE BAKED CALIBRATIONS (merchant decision 2026-07-17): once a bouquet
  // style's tuning is COMPLETE it gets baked here as a fixed adjustment blob,
  // keyed by the style key ("minimal" = the no-background style; background
  // blocks use their bg_key, e.g. "original"). Applied on top of the base bake
  // whenever that style is selected -- by the storefront section AND by the
  // admin poster review, so both always agree. The template's per-style override
  // textareas are only a dev-time scratch layer; completed styles live HERE.
  // NOTE: "original" needs no entry -- the base bake IS the Original calibration.
  var BAKED_STYLE_OVERRIDES = {};

  /** Apply a style's baked calibration (if any) onto the current state. Returns
   *  true when an entry existed. styleKey "" is normalized to "minimal". */
  function applyBakedStyleOverrides(styleKey) {
    var k = String(styleKey || "") || "minimal";
    var o = BAKED_STYLE_OVERRIDES[k];
    if (o) {
      // SEPARATION (merchant decision 2026-07-21): each baked style is its OWN
      // self-contained world and must NOT inherit the base bake's per-species
      // offsets/anchors/scales. Without this, re-baking one style (e.g. Original)
      // leaks its new keys into every other style that doesn't override them
      // (a stray june p2c7:child6 scale:0.96 bled into Minimal). A baked overlay
      // is a FULL calibration dump, so clear the stores it supplies and let the
      // overlay be authoritative -- styles stay independent no matter what else
      // gets re-baked. Layout/count keys still patch WORKING; only "original"
      // (no overlay entry) keeps the plain base bake.
      if (o.flowers) SPECIES_OFFSETS = {};
      if (o.anchors) ANCHOR_OVERRIDES = {};
      if (o.scales) SPECIES_SCALES = {};
      if (o.rotations) SPECIES_ROTATIONS = {};
      if (o.flips) SPECIES_FLIPS = {};
      if (o.directions) SPECIES_DIRECTIONS = {};
      if (o.slotFacings) SLOT_FACINGS = {};
      if (o.vaseClasses) VASE_CLASSES = {};
      if (o.classRows) CLASS_ROWS = {};
      if (o.cells) CELLS = {};
      if (o.cellNudges) CELL_NUDGES = {};
      setTemplateOverrides(o);
    }
    return !!o;
  }

  /** Dump one split's working template, keyed the way setTemplateOverrides expects. */
  function getTemplateJson(splitKey) {
    var t = getWorkingSplit(splitKey);
    return t ? t.slots.map(function (s) { return Object.assign({}, s); }) : [];
  }

  /**
   * The COMPLETE adjustments blob -- everything that differs from the factory built-ins:
   *   - count baselines that were patched via a loaded override ("5": [...])
   *   - every split tuned this session or loaded from a saved override ("p2c3": [...])
   *   - all per-species offsets ("flowers": { rose: { "p2c3:child2": {...} } })
   * Feed it back through setTemplateOverrides (the section's "Position overrides" setting)
   * and the same state is restored -- EXCEPT globalScale, which lives in the SECTION,
   * not here: the section appends it to exports (buildCompleteDevJson) and re-reads
   * overrides.globalScale itself; setTemplateOverrides deliberately ignores it. Any
   * new consumer must handle globalScale out-of-band the same way (orders carry it
   * as _global_scale). Tune counts/splits one after another and copy once at the
   * end -- everything touched is in here.
   */
  function getAdjustmentsJson() {
    ensureWorking();
    var out = {};
    Object.keys(WORKING.counts).forEach(function (k) {
      if (JSON.stringify(WORKING.counts[k].slots) !== JSON.stringify(DEFAULT_TEMPLATES[k].slots)) {
        out[k] = WORKING.counts[k].slots.map(function (s) { return Object.assign({}, s); });
      }
    });
    Object.keys(WORKING.splits).forEach(function (key) {
      var pc = parseSplitKey(key);
      if (!pc) return;
      var factory = deriveSplitTemplate(pc.p, pc.c, true);
      if (JSON.stringify(WORKING.splits[key].slots) !== JSON.stringify(factory.slots)) {
        out[key] = WORKING.splits[key].slots.map(function (s) { return Object.assign({}, s); });
      }
    });
    if (Object.keys(SPECIES_OFFSETS).length) {
      out.flowers = JSON.parse(JSON.stringify(SPECIES_OFFSETS));
    }
    if (Object.keys(ANCHOR_OVERRIDES).length) {
      out.anchors = JSON.parse(JSON.stringify(ANCHOR_OVERRIDES));
    }
    if (Object.keys(SPECIES_SCALES).length) {
      out.scales = JSON.parse(JSON.stringify(SPECIES_SCALES));
    }
    if (Object.keys(SPECIES_ROTATIONS).length) {
      out.rotations = JSON.parse(JSON.stringify(SPECIES_ROTATIONS));
    }
    if (Object.keys(SPECIES_FLIPS).length) {
      out.flips = JSON.parse(JSON.stringify(SPECIES_FLIPS));
    }
    if (Object.keys(SLOT_FACINGS).length) {
      out.slotFacings = JSON.parse(JSON.stringify(SLOT_FACINGS));
    }
    if (Object.keys(SPECIES_DIRECTIONS).length) {
      out.directions = JSON.parse(JSON.stringify(SPECIES_DIRECTIONS));
    }
    if (Object.keys(MARGIN_OVERRIDES).length) {
      out.margins = getMarginOverridesJson();
    }
    // 6+ people: size classes, class rows, cell slots, per-cell nudges, aim. Each is written
    // in full whenever it holds anything, so the published file stands on its own.
    if (Object.keys(VASE_CLASSES).length) out.vaseClasses = cloneJson(VASE_CLASSES);
    if (Object.keys(CLASS_ROWS).length) out.classRows = cloneJson(CLASS_ROWS);
    if (Object.keys(CELLS).length) out.cells = cloneJson(CELLS);
    if (Object.keys(CELL_NUDGES).length) out.cellNudges = cloneJson(CELL_NUDGES);
    if (AIM) out.aim = { x: AIM.x, y: AIM.y };
    if (finite(STEM_CUT)) out.stemCut = STEM_CUT;
    return out;
  }

  function resetTemplate(splitKey) {
    var pc = parseSplitKey(splitKey);
    if (!pc) return;
    ensureWorking();
    WORKING.splits[splitKey] = deriveSplitTemplate(pc.p, pc.c, true);
  }

  /**
   * Copy EVERYTHING tuned for one split onto another:
   *   - slot layout, matched by position id (parent0 -> parent0, child1 -> child1, ...)
   *   - every per-species offset keyed to the source split, remapped to the target
   *     (april@p2c1:child0 -> april@p2c2:child0)
   * Positions that only exist in the target (the extra child when copying p2c1 -> p2c2)
   * keep their derived values.
   */
  function copySplitTo(fromKey, toKey) {
    var from = getWorkingSplit(fromKey);
    var to = getWorkingSplit(toKey);
    if (!from || !to) return false;
    var byPosFrom = {};
    from.slots.forEach(function (s) { byPosFrom[s.pos] = s; });
    to.slots.forEach(function (s) {
      var src = byPosFrom[s.pos];
      if (src) { s.x = src.x; s.y = src.y; s.scale = src.scale; s.rot = src.rot; }
    });
    var prefix = fromKey + ":";
    Object.keys(SPECIES_OFFSETS).forEach(function (fk) {
      Object.keys(SPECIES_OFFSETS[fk]).forEach(function (ok) {
        if (ok.indexOf(prefix) === 0) {
          var pos = ok.slice(prefix.length);
          // only copy offsets for positions the target actually has
          if (to.slots.some(function (s) { return s.pos === pos; })) {
            SPECIES_OFFSETS[fk][toKey + ":" + pos] = Object.assign({}, SPECIES_OFFSETS[fk][ok]);
          }
        }
      });
    });
    return true;
  }

  /**
   * Copy ONLY a species' offset at one position to the same position in another
   * split (april@p2c3:child2 -> april@p2c4:child2). The target's slot layout is
   * left untouched -- this is the "copy this flower only" path.
   */
  function copyFlowerOffsetTo(fromKey, toKey, pos, flowerKey) {
    var fk = canonKey(flowerKey);
    if (!fk || !SPECIES_OFFSETS[fk] || !SPECIES_OFFSETS[fk][fromKey + ":" + pos]) return false;
    var to = getWorkingSplit(toKey);
    if (!to || !to.slots.some(function (s) { return s.pos === pos; })) return false;
    SPECIES_OFFSETS[fk][toKey + ":" + pos] = Object.assign({}, SPECIES_OFFSETS[fk][fromKey + ":" + pos]);
    return true;
  }

  /**
   * Copy ALL of one species' offsets from one split into another, for every position
   * both splits share (april@p2c3:child0..2 -> april@p2c9:child0..2). Positions only
   * the target has keep their state; base layout data is never touched. Returns the
   * number of offsets copied. The "start p2c9's april from my p2c3 tuning" tool.
   */
  function copyFlowerOffsetsFrom(fromKey, toKey, flowerKey) {
    var fk = canonKey(flowerKey);
    var byKey = SPECIES_OFFSETS[fk];
    if (!fk || !byKey || fromKey === toKey) return 0;
    var to = getWorkingSplit(toKey);
    if (!to) return 0;
    var prefix = fromKey + ":";
    var n = 0;
    Object.keys(byKey).forEach(function (ok) {
      if (ok.indexOf(prefix) !== 0) return;
      var pos = ok.slice(prefix.length);
      if (to.slots.some(function (s) { return s.pos === pos; })) {
        byKey[toKey + ":" + pos] = Object.assign({}, byKey[ok]);
        n++;
      }
    });
    return n;
  }

  /**
   * Copy ONE position's tuned slot values to the same position in another split --
   * and, when flowerKey is given, that species' offset at the position too.
   */
  function copySlotTo(fromKey, toKey, pos, flowerKey) {
    var from = getWorkingSplit(fromKey);
    var to = getWorkingSplit(toKey);
    if (!from || !to) return false;
    var src = null, dst = null;
    from.slots.forEach(function (s) { if (s.pos === pos) src = s; });
    to.slots.forEach(function (s) { if (s.pos === pos) dst = s; });
    if (!src || !dst) return false;
    dst.x = src.x; dst.y = src.y; dst.scale = src.scale; dst.rot = src.rot;
    var fk = canonKey(flowerKey);
    if (fk && SPECIES_OFFSETS[fk] && SPECIES_OFFSETS[fk][fromKey + ":" + pos]) {
      SPECIES_OFFSETS[fk][toKey + ":" + pos] = Object.assign({}, SPECIES_OFFSETS[fk][fromKey + ":" + pos]);
    }
    return true;
  }

  /**
   * Apply a saved adjustments blob on top of the built-ins. Accepted keys:
   *   "<count>"        -- patch the count baseline (affects every split derived from it)
   *   "p<P>c<C>"       -- patch (or create) that split's layout
   *   "<count>p1"      -- legacy 1-parent key, mapped to "p1c<count-1>"
   *   "flowers"        -- per-species offsets, merged into SPECIES_OFFSETS
   * Slot patches are arrays matching slot order; partial objects merge onto the slot.
   * Count keys apply before split keys so fresh splits derive from the patched baseline.
   */
  function setTemplateOverrides(overrides) {
    ensureWorking();
    if (!overrides || typeof overrides !== "object") return;

    function patchSlots(t, patch) {
      if (!t || !Array.isArray(patch)) return;
      patch.forEach(function (p, i) {
        if (t.slots[i] && p && typeof p === "object") Object.assign(t.slots[i], p);
      });
    }

    Object.keys(overrides).forEach(function (key) {  // pass 1: count baselines
      if (/^\d+$/.test(key)) patchSlots(WORKING.counts[key], overrides[key]);
    });
    // Pass 2: splits (incl. legacy Np1), in ascending flower count. Order is irrelevant
    // up to 12 (each split derives from the pristine built-ins), but p2c11/p2c12 derive
    // from the WORKING p2c10, so 12 has to be patched before they are first touched.
    Object.keys(overrides).map(function (key) {
      var legacy = /^(\d+)p1$/.exec(key);
      var splitKey = legacy ? splitKeyFor(1, parseInt(legacy[1], 10) - 1) : (parseSplitKey(key) ? key : null);
      var pc = splitKey && parseSplitKey(splitKey);
      return splitKey ? { key: key, splitKey: splitKey, total: pc.p + pc.c } : null;
    }).filter(Boolean).sort(function (a, b) { return a.total - b.total; }).forEach(function (e) {
      patchSlots(getWorkingSplit(e.splitKey), overrides[e.key]);
    });
    if (overrides.flowers && typeof overrides.flowers === "object") {
      Object.keys(overrides.flowers).forEach(function (fk) {
        var offs = overrides.flowers[fk];
        if (!offs || typeof offs !== "object") return;
        // canonKey so localized month keys in old blobs land on the canonical store
        // instead of creating unreachable duplicates (see LOCALE-KEY rule).
        var cfk = canonKey(fk);
        SPECIES_OFFSETS[cfk] = SPECIES_OFFSETS[cfk] || {};
        Object.keys(offs).forEach(function (ok) {
          var o = offs[ok];
          if (o && typeof o === "object") {
            var rebuilt = { dx: Number(o.dx) || 0, dy: Number(o.dy) || 0, scale: (Number(o.scale) > 0 ? Number(o.scale) : 1), rot: Number(o.rot) || 0 };
            // dz = manual draw-order bias (zIndex). Dropping it here silently broke
            // snapshot/blob round-trips of tuned stacking (fixed 2026-07-12).
            var dz = Math.round(Number(o.dz) || 0);
            if (dz) rebuilt.dz = Math.max(-20, Math.min(20, dz));
            // x / y are an ABSOLUTE spot ("june:flower2"), not an offset, and flip is a
            // boolean -- none of them survive a rebuild from a fixed dx/dy/scale/rot list.
            // Dropping them here meant every 6+ spot and every flip was destroyed the
            // moment a snapshot was loaded back, which read as "it did not save".
            if (typeof o.x === "number") rebuilt.x = o.x;
            if (typeof o.y === "number") rebuilt.y = o.y;
            if (typeof o.z === "number") rebuilt.z = o.z;
            if (o.flip) rebuilt.flip = true;
            // Same trap, twice more (2026-10-18). A "flowerN" spot that was moved but never
            // resized has NO scale and renders at defaultSpotScale() (1.38); rebuilding it
            // with scale 1 shrank it ~28% on every reload. And facing ("face left") was
            // dropped, so the flower turned back. Both read as "it is different every time".
            if (/^flower\d+$/.test(ok) && !(Number(o.scale) > 0)) delete rebuilt.scale;
            if (o.facing === "left" || o.facing === "right") rebuilt.facing = o.facing;
            SPECIES_OFFSETS[cfk][ok] = rebuilt;
          }
        });
      });
    }
    if (overrides.anchors && typeof overrides.anchors === "object") {
      Object.keys(overrides.anchors).forEach(function (fk) {
        var a = overrides.anchors[fk];
        if (a && typeof a === "object") setFlowerAnchor(fk, a.x, a.y);
      });
    }
    if (overrides.rotations && typeof overrides.rotations === "object") {
      Object.keys(overrides.rotations).forEach(function (fk) { setFlowerRotation(fk, overrides.rotations[fk]); });
    }
    if (overrides.flips && typeof overrides.flips === "object") {
      Object.keys(overrides.flips).forEach(function (fk) { setFlowerFlip(fk, !!overrides.flips[fk]); });
    }
    if (overrides.slotFacings && typeof overrides.slotFacings === "object") {
      Object.keys(overrides.slotFacings).forEach(function (k) { setSlotFacing(k, overrides.slotFacings[k]); });
    }
    if (overrides.directions && typeof overrides.directions === "object") {
      Object.keys(overrides.directions).forEach(function (fk) { setFlowerDirection(fk, overrides.directions[fk]); });
    }
    if (overrides.scales && typeof overrides.scales === "object") {
      Object.keys(overrides.scales).forEach(function (fk) { setFlowerScale(fk, overrides.scales[fk]); });
    }
    if (overrides.margins && typeof overrides.margins === "object") {
      setMargins(overrides.margins);
    }
    // ---- 6+ people. Every value is rebuilt field by field, and a field that is dropped
    // here is lost on every reload and publish (the 2026-10-18 shape), so each one that
    // getAdjustmentsJson writes is read back here.
    if (overrides.vaseClasses && typeof overrides.vaseClasses === "object") {
      Object.keys(overrides.vaseClasses).forEach(function (k) {
        var c = overrides.vaseClasses[k], ck = canonKey(k);
        if (ck && SIZE_CLASSES[c]) VASE_CLASSES[ck] = c;
      });
    }
    if (overrides.classRows && typeof overrides.classRows === "object") {
      ["L", "S"].forEach(function (cls) {
        var arr = overrides.classRows[cls];
        if (!Array.isArray(arr) || !arr.length) return;
        var row = [];
        for (var i = 0; i < Math.max(arr.length, DEFAULT_CLASS_ROWS[cls].length); i++) {
          var p = arr[i], d = rowDefault(cls, i);
          row.push({ x: (p && finite(p.x)) ? p.x : d.x, y: (p && finite(p.y)) ? p.y : d.y });
        }
        CLASS_ROWS[cls] = row;
      });
    }
    if (overrides.cells && typeof overrides.cells === "object") {
      Object.keys(overrides.cells).forEach(function (ck) {
        var c = overrides.cells[ck];
        if (!parseCellKey(ck) || !c || typeof c !== "object") return;
        var hasCounts = c.byCount && typeof c.byCount === "object";
        var hasL = c.L && typeof c.L === "object";
        if (!Array.isArray(c.m) && !hasCounts && !hasL) return;
        var slotList = function (arr) { return (Array.isArray(arr) ? arr : []).map(normCellSlot).filter(Boolean); };
        var cell = { m: slotList(c.m) };
        if (hasCounts) {
          var bc = {};
          Object.keys(c.byCount).forEach(function (n) {
            var cnt = parseInt(n, 10);
            if (String(cnt) !== n || cnt < 1 || cnt > MAX_COUNT_SLOTS || !Array.isArray(c.byCount[n])) return;
            bc[n] = slotList(c.byCount[n]);
          });
          if (Object.keys(bc).length) cell.byCount = bc;
        }
        if (hasL) {
          var Ls = {};
          Object.keys(c.L).forEach(function (k) {
            var i = parseInt(k, 10), sl = normCellSlot(c.L[k]);
            if (String(i) === k && i >= 1 && i <= MAX_L_SLOTS && sl) { delete sl.scale; if (c.L[k].scale > 0 && c.L[k].scale !== 1) sl.scale = clampRange(c.L[k].scale, 0.3, 3); Ls[k] = sl; }
          });
          if (Object.keys(Ls).length) cell.L = Ls;
        }
        CELLS[ck] = cell;
      });
    }
    if (overrides.cellNudges && typeof overrides.cellNudges === "object") {
      Object.keys(overrides.cellNudges).forEach(function (ck) {
        var byP = overrides.cellNudges[ck];
        if (!parseCellKey(ck) || !byP || typeof byP !== "object") return;
        if (byP.L && typeof byP.L === "object") {
          var outL = {};
          Object.keys(byP.L).forEach(function (pk) {
            var at = NUDGE_KEY_RE.exec(pk), n = normNudge(byP.L[pk]);
            if (at && canonKey(at[1]) && Object.keys(n).length) outL[lNudgeKey(at[1], parseInt(at[2], 10))] = n;
          });
          if (Object.keys(outL).length) (CELL_NUDGES[ck] = CELL_NUDGES[ck] || {}).L = outL;
        }
        Object.keys(byP).forEach(function (pk) {
          if (pk === "byCount" || pk === "L") return;
          var n = normNudge(byP[pk]), k = canonKey(pk);
          if (!k || !Object.keys(n).length) return;
          (CELL_NUDGES[ck] = CELL_NUDGES[ck] || {})[k] = n;
        });
        var bc = byP.byCount;
        if (!bc || typeof bc !== "object") return;
        Object.keys(bc).forEach(function (cnt) {
          var c = parseInt(cnt, 10), list = bc[cnt];
          if (String(c) !== cnt || c < 1 || c > MAX_COUNT_SLOTS || !list || typeof list !== "object") return;
          var out = {};
          Object.keys(list).forEach(function (pk) {
            var at = NUDGE_KEY_RE.exec(pk);
            if (!at && pk.indexOf("@") >= 0) return;      // "<painting>@<not a slot>"
            var k = at ? nudgeKey(at[1], parseInt(at[2], 10)) : canonKey(pk);
            var n = normNudge(list[pk]);
            if (k && Object.keys(n).length) out[k] = n;
          });
          var byCell = CELL_NUDGES[ck] = CELL_NUDGES[ck] || {};
          (byCell.byCount = byCell.byCount || {})[cnt] = out;   // an empty list still says "this count has its own nudges"
        });
      });
    }
    if (overrides.aim && typeof overrides.aim === "object") setAim(overrides.aim);
    if (finite(overrides.stemCut)) setStemCut(overrides.stemCut);
  }

  /**
   * Resolve a bouquet's flower entries into absolute design-space slots.
   * parentEntries/childEntries: arrays of { key, flowerKey, name, cmFactor, direction }, in order.
   * Assignment is positional (parent i -> "parent<i>", child j -> "child<j>"), then the
   * species offset for that flowerKey at that position is applied on top.
   * Returns: [{ ...entry, x, y, scale, rot, z, facing, splitKey, pos, tplIndex }, ...] + tiePoint.
   */
  // ---- Where a flower stands (RETIRED at 6+ people on 2026-10-20) ------------------
  // The text below describes the per-species spot + combo model that placed every 6+
  // flower. 6+ people now use the size-class cell model (section 5b): larges and smalls keep
  // their "flowerN" spot, mediums take cell slots, and the combo offsets are no longer read
  // (comboKeyFor stays: the slots still report it as splitKey).
  //
  // A flower's spots depend ONLY on how many of THAT flower there are. Three Julys
  // arrange as July-three-ways; adding a January, or removing a March, never moves them.
  //
  //   july -> "3:2"   the 2nd July, when there are three Julys
  //
  // That is why the old p2c3 split key is gone. It keyed on the TOTAL parent/child
  // counts, so every flower's position shifted whenever any other flower was added --
  // the exact thing that made a tuned July jump when January arrived.
  //
  // Two layers still, but the exception is keyed by the COMBINATION now, because that is
  // the honest name for it: "these flowers, together, need a nudge".
  //
  //   july -> "3:2"                          BASE   -- absolute spot, always
  //   july -> "january:2|july:3::3:2"        COMBO  -- only in that exact mix
  //
  // A base entry carries an ABSOLUTE x/y (fractions of the artboard) once tuned. Until
  // then it falls back to the old count template, so an untuned poster still composes
  // instead of collapsing into a heap at the origin.
  function comboKeyFor(entries) {
    var counts = {};
    (entries || []).forEach(function (e) {
      if (!e) return;
      var s = canonKey(e.flowerKey || e.key);
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.keys(counts).sort().map(function (s) { return s + ":" + counts[s]; }).join("|");
  }

  // An untuned species spot starts at the SAME size the hand-composed layouts draw at,
  // not at 1. The small layouts carry their own scale (~1.38 baked in), so starting a spot
  // at neutral made every flower shrink the moment the poster crossed from four to five.
  // Read from the LARGEST hand-composed layout so it tracks both retuning and any change
  // to the threshold, and it deliberately does NOT depend on the current count -- that
  // dependency is what made flowers resize whenever another flower was added.
  function defaultSpotScale() {
    var t = getWorkingSplit(splitKeyFor(2, SMALL_ARRANGEMENT_MAX - 2));
    var s = t && t.slots && t.slots[0] && t.slots[0].scale;
    return (typeof s === "number" && s > 0) ? s : 1.38;
  }

  // Keyed by the flower's OCCURRENCE, not by how many there are: "june:flower2" is the
  // second june, whether the poster holds two junes or five. One position per painting
  // per occurrence, full stop.
  function speciesSpot(species, index) {
    var byKey = SPECIES_OFFSETS[canonKey(species)];
    // Returned even WITHOUT x/y. A spot can carry a flip, a scale or an angle before it
    // has ever been positioned, and requiring a position meant those were read as absent.
    return (byKey && byKey["flower" + index]) || null;
  }

  // ---- 6+ people: lay a poster out by size class ----------------------------------
  // entries: the DRAWN paintings in poster order. Returns what computeBouquetSlots returns
  // at 1-5 people, plus per slot: mode "cells", cls, cellKey and slotRef
  // { kind: "L"|"S"|"M", index, mirrored, tuned, baseX, baseY } so the admin knows what a
  // drag moves. bx/by = where the head is DRAWN (nudges and the reach clamp applied),
  // wx/wy = where it WANTS to stand (what a drag should store).
  function wrapAngle(a) {
    while (a > Math.PI) a -= 2 * Math.PI;
    while (a < -Math.PI) a += 2 * Math.PI;
    return a;
  }

  // Which cell slot each medium takes. The Nth medium takes slot N, except that identical
  // paintings take the next free PAIR of slots (see ringLayout: pairs go by slot order, and a
  // pair must straddle the centre line) (so four februari draw as two pair paintings,
  // one on each side). Returns, per medium: { slot, mirrored, paired } (mirrored = the partner,
  // whose nudge is the stored one with dx and rot negated).
  function assignMediums(meds, partner, slots) {
    var n = partner.length;
    // Two slots are mirror partners only when they stand on OPPOSITE sides of the centre line.
    // A tuned cell can hold two partner-order slots on the same side (the baked L1S2 has all
    // three right of centre); identical paintings there are simply placed in order.
    function acrossCentre(s, p) { return (slots[s].x - BOUQUET_CX) * (slots[p].x - BOUQUET_CX) < 0; }
    var free = partner.map(function () { return true; });
    var out = meds.map(function () { return null; });
    function lowestFree() { for (var i = 0; i < n; i++) if (free[i]) return i; return -1; }
    meds.forEach(function (m, j) {
      if (out[j]) return;
      var twin = -1;
      for (var t = j + 1; t < meds.length; t++) if (!out[t] && meds[t].species === m.species) { twin = t; break; }
      if (twin >= 0) {
        // The next free PAIR in slot order: a slot whose partner (by order, not by position)
        // comes after it and is free too.
        for (var s = 0; s < n; s++) {
          var p = partner[s];
          if (free[s] && p > s && free[p] && acrossCentre(s, p)) {
            free[s] = false; free[p] = false;
            out[j] = { slot: s, mirrored: false, paired: true, first: s };
            out[twin] = { slot: p, mirrored: true, paired: true, first: s };
            return;
          }
        }
      }
      var f = lowestFree();
      if (f >= 0) { free[f] = false; out[j] = { slot: f, mirrored: false, paired: false, first: f }; }
    });
    return out;
  }

  function computeCellSlots(artboard, entries, tiePoint, comboKey, people, opts) {
    opts = opts || {};
    var gs = (finite(opts.globalScale) && opts.globalScale > 0) ? opts.globalScale : 1;

    // Who is what: species = the painting key, idx = which copy of it ("july:2").
    var seen = {}, countOf = {};
    var info = entries.map(function (e, i) {
      var fk = e.flowerKey || e.key, species = canonKey(fk);
      countOf[species] = (countOf[species] || 0) + 1;
      return { e: e, i: i, fk: fk, species: species, cls: getVaseClass(species) };
    });
    info.forEach(function (n) {
      n.idx = (seen[n.species] = (seen[n.species] || 0) + 1);
      n.n = countOf[n.species];
    });
    var nL = 0, nS = 0;
    info.forEach(function (n) { if (n.cls === "L") nL++; else if (n.cls === "S") nS++; });
    // Where no direction is set (not on the flower, not on its position), a flower faces AWAY from
    // the middle of the vase: right of it faces right, left of it faces left (user 2026-10-24).
    // Exactly on the middle: the painting's own direction.
    // The middle of the vase = the aim, the point under the vase opening that every stem points at
    // (it follows the vase). Not vase.mouthX: that is where the image is placed, and the image
    // carries its cast shadow, so it sits left of the vessel (0.48 vs 0.585).
    var vaseMidX = (AIM || defaultAimFor(opts.vase)).x;
    function sideFacing(x) { return x > vaseMidX + 1e-6 ? "right" : x < vaseMidX - 1e-6 ? "left" : null; }
    var meds = info.filter(function (n) { return n.cls === "M"; });
    var cellKey = cellKeyFor(entries);
    LAST_CELL_COUNT[cellKey] = meds.length;

    // ---- Larges and smalls: their OWN spot per occurrence, the same in every mix ----
    // A tuned spot is used as it is. An untuned one STARTS in its class row, and copies of the
    // same painting pair up: copy 1 + 2 are mirror images (2 mirrors 1, facing the other way),
    // 3 + 4 likewise. An odd copy (1st, 3rd, 5th) takes the next FREE place in the class row;
    // so does an even copy whose mirror image would land on top of another flower (its
    // predecessor stands on the centre line, or the spot is taken), and then it is a fresh copy
    // like any other: it inherits nothing. Row places are dealt on a stable identity (painting key, then occurrence), never
    // on where the painting happens to stand in the people list, so reordering people does
    // not swap who stands where.
    var lsInfo = info.filter(function (n) { return n.cls !== "M"; });
    var occupied = { L: [], S: [] }, rowCursor = { L: 0, S: 0 }, prev = {};
    // Global large positions: the Nth DIFFERENT large painting (copies count once, as in the mix
    // key) stands its first copy on the mix's L# when it has one.
    var lRank = {}, nextL = 0;
    lsInfo.forEach(function (n) {
      if (n.cls !== "L") return;
      var u = classKeyOfFlower(n.fk);
      // Only L1 is a shared place (user 2026-10-23: "on L2 all flowers have their respective position").
      if (!(u in lRank)) { lRank[u] = ++nextL; if (nextL === 1) n.lslot = 1; }
    });
    lsInfo.forEach(function (n) {
      n.base = speciesSpot(n.species, n.idx);
      n.ls = n.lslot ? getCellLBase(cellKey, n.lslot) : null;
      n.lown = !!(n.lslot && getCellLSlot(cellKey, n.lslot));
      n.hasPos = !!n.ls || !!(n.base && finite(n.base.x) && finite(n.base.y));
      if (n.ls) occupied[n.cls].push({ x: n.ls.x, y: n.ls.y });
      else if (n.hasPos) occupied[n.cls].push({ x: n.base.x, y: n.base.y });
    });
    function isTaken(cls, x, y) {
      return occupied[cls].some(function (o) { return Math.abs(o.x - x) < 0.02 && Math.abs(o.y - y) < 0.02; });
    }
    function nextFreeRowPlace(cls) {
      for (;;) {
        var rp = getClassRow(cls, rowCursor[cls]++);
        if (!isTaken(cls, rp.x, rp.y)) return rp;
      }
    }
    lsInfo.slice().sort(function (a, b) {
      return a.species < b.species ? -1 : a.species > b.species ? 1 : a.idx - b.idx;
    }).forEach(function (n) {
      var base = n.base, hasPos = n.hasPos, p = prev[n.species], mirrorStart = false, x, y;
      var ls = n.ls, lnd = ls ? getCellLNudge(cellKey, n.species, n.lslot) : null;
      if (ls) { x = ls.x; y = ls.y; }
      else if (hasPos) { x = base.x; y = base.y; }
      else if (p && n.idx % 2 === 0 && Math.abs(p.x - BOUQUET_CX) >= 0.03 && !isTaken(n.cls, 2 * BOUQUET_CX - p.x, p.y)) {
        x = 2 * BOUQUET_CX - p.x; y = p.y; mirrorStart = true;
        occupied[n.cls].push({ x: x, y: y });
      } else {
        var rp = nextFreeRowPlace(n.cls); x = rp.x; y = rp.y;
        occupied[n.cls].push({ x: x, y: y });
      }
      var scale = (base && finite(base.scale)) ? base.scale : (mirrorStart ? p.scale : defaultSpotScale());
      var rot = (base && finite(base.rot)) ? base.rot : (mirrorStart ? -p.rot : 0);
      var dz = (base && finite(base.dz)) ? base.dz : (mirrorStart ? p.dz : 0);
      // Facing: a stated facing wins; a mirrored copy faces the OTHER way from what its
      // partner shows; otherwise the painting's own flip decides.
      var painted = facingFor(n.fk, n.e.direction), flip;
      // On L#: the flower's own direction there, then the position's, then the painting spot's.
      var lFacing = ls ? (lnd.facing || ls.facing || null) : null;
      if (ls) {
        scale = scale * lnd.scale * (ls.scale || 1);
        rot = rot + lnd.rot;
        dz = (finite(ls.dz) ? ls.dz : dz) + lnd.dz;
      }
      var side = sideFacing(x + (lnd ? lnd.dx : 0));
      if (lFacing) flip = lFacing !== painted;
      else if (base && base.facing) flip = base.facing !== painted;
      else if (side) flip = side !== painted;
      else flip = (!!(base && base.flip) !== getFlowerFlip(n.fk));
      var shownFacing = flip ? (painted === "left" ? "right" : "left") : painted;
      prev[n.species] = { x: x, y: y, scale: scale, rot: rot, dz: dz, flip: flip, shown: shownFacing };
      n.rec = {
        kind: n.cls, index: n.idx, mirrored: mirrorStart, tuned: hasPos, lslot: n.lslot || 0, lset: !!n.lown,
        x: x, y: y, dx: lnd ? lnd.dx : 0, dy: lnd ? lnd.dy : 0, scale: scale, nscale: 1, rot: rot,
        zBias: dz + CLASS_BIAS[n.cls], z: (base && finite(base.z)) ? base.z : n.i, flip: flip
      };
    });

    // ---- Mediums: the cell's slots, then the painting's nudge in this cell ----
    // With a per-count list for this many mediums (1-5, Matilda's hand-composed layouts) the
    // mediums take that list, and the nudge is the per-count one for that painting in that slot.
    // Only at 1-5 PEOPLE, and in poster order, the Nth medium slot N, exactly like her old layouts
    // (no mirrored pairs: only singles are drawn there, so a per-count nudge is never negated).
    // A 6+ poster always takes `m` / the ring with its mirrored pairs, however few mediums it has.
    var perCount = hasCountSlots(cellKey, meds.length) ? meds.length : 0;
    (LAST_PER_COUNT[cellKey] = LAST_PER_COUNT[cellKey] || {})[meds.length] = !!perCount;
    var slots = getCellSlots(cellKey, meds.length, people);
    var tunedLen = perCount ? countSlotsOf(cellKey, perCount).length : ((CELLS[cellKey] && CELLS[cellKey].m) ? CELLS[cellKey].m.length : 0);
    // Poster order (Nth medium on slot N, no pairs) only at 1-5 people, as her old layouts did. A 6+
    // poster standing on a per-count list keeps its mirrored pairs of identical paintings: the list
    // gives the places, the 6+ rule decides who stands where.
    // NO medium pairs for now (user 2026-10-23: "ignore pair on medium"): at any number of people
    // the Nth medium stands on slot N with its own nudge as saved, never mirrored. assignMediums
    // is kept (unused) in case pairs come back.
    var assign = meds.map(function (m, j) { return { slot: j, mirrored: false, paired: false, first: j }; });
    meds.forEach(function (n, j) {
      var a = assign[j], sl = slots[a.slot];
      var nd = perCount ? getCellNudge(cellKey, n.species, perCount, a.slot) : getCellNudge(cellKey, n.species);
      // A stated facing wins (the partner of a pair gets the opposite one); then the facing the
      // slot itself asks for; a pair otherwise faces OUTWARD from where its first slot stands,
      // the partner the other way (so they stay mirrored even when one slot has been moved
      // across the centre line); a lone medium follows the painting's own flip.
      // NO automatic direction (user 2026-10-23: "we do not want an automatic change, we put the
      // direction"). Only directions set by hand turn a medium: the flower's own direction on this
      // position (Flower unique) wins, then the direction of the position itself (Global position:
      // every flower standing there faces that way, user 2026-10-23 "the Faces is not connected to
      // the position"), else the painting's own direction. Gone: identical mediums facing outward,
      // a mirrored partner facing the other way.
      var stated = nd.facing || sl.facing || sideFacing(sl.x + nd.dx);
      var flip = stated ? (stated !== facingFor(n.fk, n.e.direction)) : getFlowerFlip(n.fk);
      n.rec = {
        kind: "M", index: a.slot, mirrored: a.mirrored, tuned: a.slot < tunedLen, perCount: perCount,
        x: sl.x, y: sl.y, dx: a.mirrored ? -nd.dx : nd.dx, dy: nd.dy,
        scale: defaultSpotScale(), nscale: nd.scale * (sl.scale || 1), rot: a.mirrored ? -nd.rot : nd.rot,
        zBias: (sl.dz || 0) + CLASS_BIAS.M + nd.dz, z: n.i, flip: flip
      };
    });

    // ---- Final transform: scale, aim, automatic stem angle ----
    // Pass 1, per flower: where the head stands, how long its stem is, the reach clamp, and where
    // inside the vase its stem ends. Pass 2: untuned mediums the clamp pushed onto another head
    // swing aside around the aim. Pass 3: the angle that points each stem at its end point.
    var aim = AIM || defaultAimFor(opts.vase);
    var aimX = artboard.x + aim.x * artboard.w, aimY = artboard.y + aim.y * artboard.h;
    // The inside of the vessel, where every stem must END (panel fractions): from the mouth down
    // to the floor (vaseFloor, from the drawn vase), and INNER_HALF either side of the aim, which
    // follows the vase. The drawn image is no measure of the vessel's width: it carries the cast
    // shadow (the mouth sits at 10% of the file). Only with a floor, i.e. when the vase is drawn.
    var vbox = null;
    if (finite(opts.vaseFloor)) {
      var vz = opts.vase || {}, top = Number(vz.mouthY), half = Number(vz.innerHalfWidth);
      if (!finite(top)) top = aim.y - 0.2;
      if (!finite(half) || half <= 0) half = VASE_INNER_HALF;
      vbox = { x0: aim.x - half, x1: aim.x + half, y0: Math.min(top, opts.vaseFloor), y1: opts.vaseFloor };
    }
    var BX0 = vbox && artboard.x + vbox.x0 * artboard.w, BX1 = vbox && artboard.x + vbox.x1 * artboard.w;
    var BY0 = vbox && artboard.y + vbox.y0 * artboard.h, BY1 = vbox && artboard.y + vbox.y1 * artboard.h;
    var geo = info.map(function (n) {
      var r = n.rec, e = n.e;
      var g = { n: n, wx: r.x + r.dx, wy: r.y + r.dy, scale: r.scale * r.nscale * getFlowerScale(n.fk), clamped: false, floored: false, spread: false, len: 0 };
      g.x = artboard.x + g.wx * artboard.w; g.y = artboard.y + g.wy * artboard.h;
      var se = STEM_ENDS[n.species] || (opts.stemEnds && opts.stemEnds[n.species]) || null;
      if (!se) return g;
      var anchor = getFlowerAnchor(n.fk) || getFlowerOverride(e.key).stemAnchor || { x: 0.5, y: 0.5 };
      var ax = finite(anchor.x) ? anchor.x : 0.5, ay = finite(anchor.y) ? anchor.y : 0.5;
      var box = FLOWER_BASE_SIZE * g.scale * gs * parseCmFactor(e.cmFactor);
      var aspect = (finite(se.aspect) && se.aspect > 0) ? se.aspect : 1;
      var drawW = aspect >= 1 ? box : box * aspect, drawH = aspect >= 1 ? box / aspect : box;
      // The vector from the anchor to the stem end, as the canvas will draw it: mirrored
      // first (drawFlowerAt flips, then rotates).
      g.vx = (se.x - ax) * drawW; g.vy = (se.y - ay) * drawH;
      if (r.flip) g.vx = -g.vx;
      g.len = Math.hypot(g.vx, g.vy);
      // A head stands EXACTLY where its slot, spot and nudge put it (2026-10-21, fix round 2: the
      // reach clamp and the floor lift moved bigger flowers further and wrecked her positions).
      // Only the stem ANGLE is automatic; a stem longer than the vessel is clipped at the floor
      // by renderPoster. `clamped` and `floored` stay false (kept for callers).
      fitStem(g);
      return g;
    });
    function stemEndsInside(g, th) {
      var ex = g.x + g.len * Math.cos(th), ey = g.y + g.len * Math.sin(th), eps = 1e-6;
      return ex >= BX0 - eps && ex <= BX1 + eps && ey >= BY0 - eps && ey <= BY1 + eps;
    }
    // The stem direction closest to "at the aim" whose end lies inside the vessel; null if none.
    function insideTheta(g) {
      var ta = Math.atan2(aimY - g.y, aimX - g.x);
      for (var k = 0; k <= 360; k++) {
        for (var sgn = 1; sgn >= -1; sgn -= 2) {
          var th = ta + sgn * k * Math.PI / 360;
          if (stemEndsInside(g, th)) return th;
          if (!k) break;
        }
      }
      return null;
    }
    // Point the stem at the aim when its end then lies inside the vessel; else turn it the least
    // that puts the end inside; and when no turn can (the stem is longer than the vessel is deep
    // from where the head stands), point it at the middle of the floor, straight down into the
    // vessel: renderPoster clips the part below the floor. The head never moves.
    function fitStem(g) {
      g.theta = null;
      if (!vbox || !(g.len > 0)) return;
      var th = insideTheta(g);
      g.theta = th !== null ? th : Math.atan2(BY1 - g.y, aimX - g.x);
    }
    // No flower is ever pushed aside (user 2026-10-23: "we do not want that"): until then an untuned
    // medium closer than MEDIUM_MIN_GAP to another head was swung around the aim, so adding a flower
    // moved others. Every head stands exactly where its position, spot and nudge put it.
    var out = geo.map(function (g) {
      var n = g.n, r = n.rec, e = n.e;
      var wx = g.wx, wy = g.wy, scale = g.scale, headX = g.x, headY = g.y;
      var rot = r.rot, clamped = g.clamped, aimed = false;
      var stemEnd = null;
      if (g.len > 1e-6 && g.theta !== null && g.theta !== undefined) {
        rot = wrapAngle(g.theta - Math.atan2(g.vy, g.vx)) + r.rot; aimed = true;
      } else if (g.len > 1e-6) {
        var dx = aimX - headX, dy = aimY - headY;
        if (dx || dy) { rot = wrapAngle(Math.atan2(dy, dx) - Math.atan2(g.vy, g.vx)) + r.rot; aimed = true; }
      }
      if (aimed) {
        var th2 = rot + Math.atan2(g.vy, g.vx);   // as drawn (a rotation nudge included)
        stemEnd = { x: (headX + g.len * Math.cos(th2) - artboard.x) / artboard.w, y: (headY + g.len * Math.sin(th2) - artboard.y) / artboard.h };
      }
      var bx = (headX - artboard.x) / artboard.w, by = (headY - artboard.y) / artboard.h;
      return {
        key: e.key, flowerKey: n.fk, name: e.name, cmFactor: e.cmFactor, direction: e.direction,
        flip: r.flip,
        stemKey: e.stemKey || null,
        x: headX, y: headY, scale: scale, rot: rot,
        z: r.z, zBias: r.zBias,
        facing: facingFor(n.fk, e.direction),
        species: n.species, speciesIndex: n.idx, speciesCount: n.n,
        splitKey: comboKey, pos: "flower" + n.idx, comboPos: comboKey + "::flower" + n.idx,
        tplIndex: n.i,
        bx: bx, by: by, wx: wx, wy: wy, tuned: r.tuned,
        mode: "cells", cls: n.cls, cellKey: cellKey, clamped: clamped, aimed: aimed, floored: g.floored, spread: g.spread, stemEnd: stemEnd,
        slotRef: { kind: r.kind, index: r.index, mirrored: r.mirrored, tuned: r.tuned, baseX: r.x, baseY: r.y, perCount: r.perCount || 0, lslot: r.lslot || 0, lset: !!r.lset }
      };
    });

    return {
      slots: out, tiePoint: tiePoint, comboKey: comboKey, mode: "cells", cellKey: cellKey, people: people,
      counts: { L: nL, S: nS, M: meds.length }, aim: { x: aim.x, y: aim.y }, vaseBox: vbox
    };
  }

  /**
   * opts (optional): { peopleCount, globalScale, stemEnds, vase }
   *   peopleCount  how many PEOPLE the poster is for (a pair draws one image for two people).
   *                Given: the cell model at every count, 1-12 (at 1-5 people mediums take a
   *                per-count list in poster order). Absent (old callers): the hand-composed
   *                layouts at 1-5 paintings, the cell model above.
   *   globalScale  the poster's global scale (stem length depends on the drawn size)
   *   stemEnds     { paintingKey: { x, y, aspect } } measured from the images
   *   vase         input.vase, only to shift the default aim with the vase
   *   vaseFloor    panel-height fraction no stem may end below (renderPoster: just above the
   *                drawn vase's bottom). With it every stem ends inside the vessel: between
   *                vase.mouthY and the floor, within VASE_INNER_HALF (or vase.innerHalfWidth)
   *                of the aim. Absent = no such limit (Node tests, a poster without a vase)
   */
  // How far down a stem may end: just above the bottom of the vase as renderPoster draws it
  // (top-centre mouth on mouthX/mouthY, width a share of the panel, height from the image), as a
  // fraction of the panel height. Null without the vase image (no floor then).
  var VASE_FLOOR_INSET = 0.08;   // share of the vase's drawn height kept clear above its bottom edge
  function vaseFloorFor(vase, imagesByKey, artboard) {
    var img = vase && vase.key && imagesByKey && imagesByKey[vase.key];
    var iw = img && (img.naturalWidth || img.width), ih = img && (img.naturalHeight || img.height);
    if (!iw || !ih || !artboard || !artboard.h) return null;
    var wf = Number(vase.width); if (!isFinite(wf) || wf <= 0) wf = 0.3;
    var my = Number(vase.mouthY); if (!isFinite(my)) my = 0.62;
    var iay = Number(vase.imgMouthY); if (!isFinite(iay)) iay = 0;
    my = Math.min(1, Math.max(0, my)); iay = Math.min(1, Math.max(0, iay));
    var vhF = artboard.w * wf * (ih / iw) / artboard.h;   // drawn vase height, as a share of the panel height
    return my + (1 - iay - VASE_FLOOR_INSET) * vhF;
  }

  function computeBouquetSlots(artboard, parentEntries, childEntries, opts) {
    parentEntries = (parentEntries || []).slice(0, 2);
    childEntries = (childEntries || []).slice(0, MAX_LAYOUT_FLOWERS - 2);
    var entries = parentEntries.concat(childEntries);
    var total = entries.length;
    var tiePoint = { x: artboard.x + artboard.w * TIE_POINT_FRACTION.x, y: artboard.y + artboard.h * TIE_POINT_FRACTION.y };
    if (total === 0) return { slots: [], tiePoint: tiePoint };

    var splitKey = splitKeyFor(parentEntries.length, childEntries.length);
    var comboKey = comboKeyFor(entries);
    var hasPeople = !!(opts && finite(opts.peopleCount) && opts.peopleCount > 0);
    var people = hasPeople ? opts.peopleCount : total;

    // ---- Every poster that states its people count (2026-10-21): the cell model, 1-12 ----
    // Matilda's hand-composed 1-5 layouts live on as the per-count medium slots of the cells
    // (cells.L0S0.byCount), so a 1-5 poster of mediums stands where it stood. Pair paintings still
    // only appear from six people (resolveRepeats).
    if (hasPeople) return computeCellSlots(artboard, entries, tiePoint, comboKey, people, opts);

    // ---- OLD CALLERS (no people count): the hand-composed layouts at 1-5 paintings ----
    // Hand-composed per count, the way the bouquet does it: each count has its own
    // layout (p1c0, p2c0, p2c1, p2c2) and a flower carries an offset at each position
    // within it. Four flowers or fewer is few enough to art-direct exactly, and only v1
    // paintings appear at these counts.
    if (people <= SMALL_ARRANGEMENT_MAX && total <= SMALL_ARRANGEMENT_MAX) {
      var template = getWorkingSplit(splitKey);
      if (!template) return { slots: [], tiePoint: tiePoint };
      var byPos = {};
      template.slots.forEach(function (sl, i) { byPos[sl.pos] = { tpl: sl, tplIndex: i }; });

      var smallSlots = [];
      entries.forEach(function (e, i) {
        var pos = (i < 2 ? "parent" + i : "child" + (i - 2));
        var hit = byPos[pos];
        if (!hit) return;
        var t = hit.tpl;
        var fk = e.flowerKey || e.key;
        // NO flowerN base here: at these counts the base position IS the layout's slot
        // (p2c2), shared by whatever flower stands there. All this adds is THIS flower's
        // offset at that slot.
        var off = getFlowerOffsetAt(fk, splitKey + ":" + pos);
        smallSlots.push({
          key: e.key, flowerKey: fk, name: e.name, cmFactor: e.cmFactor, direction: e.direction,
          // A slot that states which way it wants a flower to face decides the mirror.
          // Where it says nothing, the hand flip on the painting still rules.
          flip: (function () {
            var wants = getSlotFacing(splitKey + ":" + pos) || off.facing;
            if (wants) return wants !== facingFor(fk, e.direction);
            return (!!off.flip !== getFlowerFlip(fk));
          })(),
          stemKey: e.stemKey || null,
          x: artboard.x + (t.x + off.dx) * artboard.w,
          y: artboard.y + (t.y + off.dy) * artboard.h,
          // The painting's own default scale and angle sit UNDER everything, so resizing
          // a flower does not depend on how many others are on the poster.
          scale: (t.scale || 1) * (off.scale || 1) * getFlowerScale(fk),
          rot: (t.rot || 0) + (off.rot || 0) + getFlowerRotation(fk),
          z: t.z, zBias: off.dz || 0,
          facing: facingFor(fk, e.direction),
          splitKey: splitKey, pos: pos, tplIndex: hit.tplIndex,
          bx: t.x + off.dx, by: t.y + off.dy
        });
      });
      return { slots: smallSlots, tiePoint: tiePoint, comboKey: comboKey, templateSource: template.source, mode: "count" };
    }

    // ---- 6+ PEOPLE: the size-class cell model (section 5b) ----
    return computeCellSlots(artboard, entries, tiePoint, comboKey, people, opts);
  }

  // ============================================================
  // 6) DRAW: fit image into placement (NO SQUISH), rotated around its anchor
  // ============================================================
  function parseCmFactor(val) {
    if (val == null) return 1;
    var n = parseFloat(String(val).replace(",", "."));
    return (isFinite(n) && n > 0) ? n : 1;
  }

  function drawFlowerAt(ctx, img, slot, style, cmFactor, anchor, flip, globalScale) {
    if (!img || !(img.naturalWidth || img.width)) return;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    var cf = parseCmFactor(cmFactor);
    var gs = (typeof globalScale === "number" && globalScale > 0) ? globalScale : 1;
    var box = FLOWER_BASE_SIZE * (slot.scale || 1) * gs;
    var scale = Math.min(box / iw, box / ih) * cf;
    var drawW = iw * scale;
    var drawH = ih * scale;
    var ax = (anchor && typeof anchor.x === "number") ? anchor.x : 0.5;
    var ay = (anchor && typeof anchor.y === "number") ? anchor.y : 0.5;

    ctx.save();
    ctx.translate(slot.x, slot.y);
    if (slot.rot) ctx.rotate(slot.rot);
    if (style.flowerShadow) {
      ctx.shadowColor = "rgba(0,0,0,0.10)";
      ctx.shadowBlur = 14;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
    }
    if (flip) ctx.scale(-1, 1);
    ctx.drawImage(img, -drawW * ax, -drawH * ay, drawW, drawH);
    ctx.restore();
  }

  /**
   * Draw a stem stretched from the flower's center (x0,y0) to the tie point (x1,y1).
   * Stem asset contract: a mostly-vertical image -- TOP = where it meets the flower
   * head, BOTTOM = the cut end. Width uses the same scale factor as the head so
   * thickness stays proportional; length stretches to the actual distance.
   */
  function drawStemStretched(ctx, img, x0, y0, x1, y1, widthScale, flip) {
    if (!img || !(img.naturalWidth || img.width)) return;
    var iw = img.naturalWidth || img.width;
    var dx = x1 - x0, dy = y1 - y0;
    var len = Math.hypot(dx, dy);
    if (len < 1) return;
    var theta = Math.atan2(-dx, dy); // rotate local +y axis onto the head->tie direction
    var w = iw * widthScale;
    ctx.save();
    ctx.translate(x0, y0);
    ctx.rotate(theta);
    if (flip) ctx.scale(-1, 1);
    ctx.drawImage(img, -w / 2, 0, w, len);
    ctx.restore();
  }

  function drawImageContain(ctx, img, rect) {
    if (!img || !(img.naturalWidth || img.width)) return;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    var scale = Math.min(rect.w / iw, rect.h / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    var dx = rect.x + (rect.w - dw) / 2;
    var dy = rect.y + (rect.h - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  /**
   * Draw a background image into `rect` with anchor (0..1) and a zoom
   * factor on top of "contain" scaling. Image is always clipped to rect.
   */
  function drawBackgroundFit(ctx, img, rect, anchor, scale) {
    if (!img || !(img.naturalWidth || img.width)) return;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    var s = (typeof scale === "number" && scale > 0) ? scale : 1;
    // COVER, not contain. The vase's background is a painted scene -- wall, table,
    // horizon -- so it has to fill the panel edge to edge; a contain fit letterboxed it
    // and left bands of the flat rect colour showing through the artwork. Overflow is
    // clipped to the rect below, and the anchor decides which part survives the crop.
    // (The bouquet still contains: its backgrounds are meant to sit inside the panel.)
    var baseScale = Math.max(rect.w / iw, rect.h / ih);
    var fullScale = baseScale * s;
    var dw = iw * fullScale;
    var dh = ih * fullScale;
    var ax = (anchor && typeof anchor.x === "number") ? anchor.x : 0.5;
    var ay = (anchor && typeof anchor.y === "number") ? anchor.y : 0.5;
    var dx = rect.x + (rect.w - dw) * ax;
    var dy = rect.y + (rect.h - dh) * ay;
    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();
  }

  // ============================================================
  // 7) TEXT HELPERS
  // ============================================================
  function drawSpacedText(ctx, text, centerX, y, letterSpacing) {
    var chars = String(text || "").split("");
    var totalWidth = 0;
    for (var c = 0; c < chars.length; c++)
      totalWidth += ctx.measureText(chars[c]).width + letterSpacing;
    totalWidth = Math.max(0, totalWidth - letterSpacing);
    var x = centerX - totalWidth / 2;
    for (var c2 = 0; c2 < chars.length; c2++) {
      ctx.fillText(chars[c2], x, y);
      x += ctx.measureText(chars[c2]).width + letterSpacing;
    }
  }

  /**
   * Paper/shadow texture overlay -- drawn ONCE, multiplied over the whole poster,
   * scaled "FILL"/COVER: the image keeps its own aspect ratio and is scaled to fully
   * cover the poster, cropping the overflow (centered) -- never squashed to the poster
   * shape (merchant 2026-07-18). No tiling, so no seams. A ~1px overshoot guarantees
   * full edge coverage against buffer rounding.
   */
  function drawTextureOverlay(ctx, textureImg, style, designH, bleedD) {
    var iw = textureImg && (textureImg.naturalWidth || textureImg.width);
    var ih = textureImg && (textureImg.naturalHeight || textureImg.height);
    if (!iw || !ih) return;
    // On a print export the canvas is the full sheet (trim + bleed), so the
    // texture has to cover the bleed as well -- otherwise the strip that gets
    // trimmed off is bare paper colour and the seam shows if the cut drifts.
    // Same centre, so the texture inside the trim only changes by ~1% of scale.
    var bx = (bleedD && bleedD.x) || 0, by = (bleedD && bleedD.y) || 0;
    ctx.save();
    ctx.globalCompositeOperation = style.textureComposite || "multiply";
    ctx.globalAlpha = (typeof style.textureOpacity === "number") ? style.textureOpacity : 0.4;
    var targetW = DESIGN_W + bx * 2 + 1, targetH = designH + by * 2 + 1; // +1 overshoot -> no edge gap
    var scale = Math.max(targetW / iw, targetH / ih);  // COVER
    var dw = iw * scale, dh = ih * scale;
    ctx.drawImage(textureImg, (DESIGN_W - dw) / 2, (designH - dh) / 2, dw, dh); // centered crop
    ctx.restore();
  }

  /** Width of `text` as drawSpacedText will draw it (per-char widths + tracking). */
  function measureSpacedWidth(ctx, text, letterSpacing) {
    var chars = String(text || "").split("");
    var w = 0;
    for (var i = 0; i < chars.length; i++) w += ctx.measureText(chars[i]).width + letterSpacing;
    return Math.max(0, w - letterSpacing);
  }

  /** Greedy word-wrap honoring the letter spacing; ctx.font must already be set. */
  function wrapSpacedText(ctx, text, maxWidth, letterSpacing) {
    var words = String(text || "").split(" ");
    var lines = [];
    var cur = "";
    for (var i = 0; i < words.length; i++) {
      var attempt = cur ? cur + " " + words[i] : words[i];
      if (cur && measureSpacedWidth(ctx, attempt, letterSpacing) > maxWidth) {
        lines.push(cur);
        cur = words[i];
      } else {
        cur = attempt;
      }
    }
    if (cur) lines.push(cur);
    return lines;
  }

  /**
   * Wrap `text` into AT MOST maxRows rows of maxWidth, shrinking the font until it
   * fits (designer rule: names/flowers may take 2 rows, never more). letterSpacing
   * is em-based so it shrinks with the font. Returns { px, lines }.
   */
  function fitWrapped(ctx, text, maxWidth, startPx, minPx, maxRows, fontFamily, lsEm) {
    var px = startPx;
    var lines;
    while (px >= minPx) {
      ctx.font = "400 " + px + "px " + fontFamily + ", RockwellPoster";
      var ls = lsEm * px;
      lines = wrapSpacedText(ctx, text, maxWidth, ls);
      var fits = lines.length <= maxRows;
      for (var i = 0; fits && i < lines.length; i++) {
        if (measureSpacedWidth(ctx, lines[i], ls) > maxWidth) fits = false; // single over-long word
      }
      if (fits) return { px: px, lines: lines };
      px -= 0.5;
    }
    ctx.font = "400 " + minPx + "px " + fontFamily + ", RockwellPoster";
    return { px: minPx, lines: wrapSpacedText(ctx, text, maxWidth, lsEm * minPx).slice(0, maxRows) };
  }

  // ============================================================
  // 8) RENDER POSTER
  // ============================================================
  // Draw order: manual zBias layer FIRST (dev-forced forward/back), then painter's by
  // resolved y (lower = more in front), then template z as the final tiebreak. Pulled out of
  // renderPoster unchanged so the layout tests use the comparator the poster really uses.
  function orderSlotsForDrawing(slots) {
    var byY = slots.slice().sort(function (a, b) { return (a.y - b.y) || (a.z - b.z); });
    byY.forEach(function (s, i) { s._rank = i; });
    var slotsByZ = slots.slice().sort(function (a, b) {
      // The tie is the whole point: moving a flower one place lands it exactly level with
      // its neighbour, and falling back to y there would restore the original order and
      // the step would do nothing. Whoever was pushed harder wins the tie.
      return ((a._rank + (a.zBias || 0)) - (b._rank + (b.zBias || 0)))
        || ((a.zBias || 0) - (b.zBias || 0))
        || (a.y - b.y) || (a.z - b.z);
    });
    return slotsByZ;
  }

  function renderPoster(ctx, imagesByKey, input, styleOverride) {
    // MARGIN_OVERRIDES win over both defaults and the caller's style (which only sets colors).
    var style = Object.assign({}, DEFAULT_STYLE, styleOverride || {}, MARGIN_OVERRIDES);
    var sizeKey = (input && input.sizeKey) || "50x70";
    // Per-size footer reserve (see SIZE_FOOTER_BUMP). Applied AFTER margin overrides so it
    // composes with any dev tuning; only affects the visible rect + footer, never the
    // frozen bouquet frame below.
    var _fBump = SIZE_FOOTER_BUMP[sizeKey];
    if (_fBump && typeof style.footerHeightPct === "number") {
      style = Object.assign({}, style, { footerHeightPct: style.footerHeightPct + _fBump });
    }
    var designH = designHeightFor(sizeKey);
    var artboard = getArtboardRect(style, designH);

    // ---- The panel takes the BACKGROUND'S aspect ----
    // The available box is a different shape at every print size (0.90 / 0.98 / 0.86),
    // because its width is fixed while its height follows the paper. So the same settings
    // composed differently on each size, the background had to be cropped to fit, and the
    // vase started clipping at a different zoom on each (50% / 46% / 52%).
    // Sizing the panel to the artwork instead makes it the SAME SHAPE everywhere: the
    // background is never cropped, and every size is the identical composition scaled up
    // or down. These backgrounds are taller than all three boxes, so in practice the panel
    // fills the height and narrows -- the footer and the title never move.
    var _bgFit = imagesByKey && imagesByKey[BG_KEY];
    var _bw = _bgFit && (_bgFit.naturalWidth || _bgFit.width);
    var _bh = _bgFit && (_bgFit.naturalHeight || _bgFit.height);
    var panelAR = (_bw && _bh) ? (_bh / _bw) : (style.panelAspect || 1.1655); // height / width
    var _pw = artboard.h / panelAR;
    if (_pw > artboard.w) _pw = artboard.w;
    var _ph = _pw * panelAR;
    if (_ph > artboard.h) { _ph = artboard.h; _pw = _ph / panelAR; }
    artboard = { x: artboard.x + (artboard.w - _pw) / 2, y: artboard.y, w: _pw, h: _ph };

    var footer = getFooterRect(style, artboard, designH);
    // Print exports render onto the full sheet: design coords 0..DESIGN_W are the
    // TRIM, and the bleed sits outside that in negative / past-the-edge coords.
    // Everything below draws in trim coords as before -- only the two layers that
    // are meant to reach the paper edge (background fill, texture) use this.
    var bleedD = bleedDesign(input);

    // The flowers are laid out in the PANEL, not in the bouquet's frozen frame. That
    // frame exists to protect the bouquet's baked calibration from rect changes, but here
    // it would do the opposite of what is wanted: the flowers would keep a fixed size
    // while the panel around them changed shape per print size, so they would sit
    // differently -- and get clipped -- on each. Tying them to the panel is what makes
    // every size the same composition, scaled.
    var layoutArtboard = artboard;
    // A vase's own zoom (vase.scale, the vase block's "Size" setting): one vase painted a bit
    // smaller than the others can be drawn larger. Applied here, once, so the drawing, the stem
    // cut and the vase floor all see the same vase.
    if (input && input.vase && finite(Number(input.vase.scale)) && Math.abs(Number(input.vase.scale) - 1) > 1e-6) {
      input = Object.assign({}, input, { vase: scaledVase(input.vase, imagesByKey, artboard) });
    }

    // Background — fill the ENTIRE physical buffer (identity transform), not just the
    // design-space rect: buffer rounding can leave a sub-pixel row past designH that
    // would otherwise stay transparent and show the page behind the canvas as a line.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.fillStyle = style.posterBgColor || "#FFFFFF";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();

    // Artboard rect
    ctx.fillStyle = input && input.rectColor ? input.rectColor : style.rectColor || "#EDEBE7";
    ctx.fillRect(artboard.x, artboard.y, artboard.w, artboard.h);

    // Background fill image (greenery/filler), fit inside artboard.
    var bgImg = imagesByKey && imagesByKey[BG_KEY];
    if (bgImg) {
      var bp = input && input.bgPlacement;
      if (bp) {
        drawBackgroundFit(ctx, bgImg, artboard, { x: bp.anchorX, y: bp.anchorY }, bp.scale);
      } else {
        drawImageContain(ctx, bgImg, artboard);
      }
    }

    // Flowers — split by role (parent/child), preserving slotIndex order within each.
    var flowers = Array.isArray(input && input.flowers) ? input.flowers.slice(0, MAX_LAYOUT_FLOWERS) : [];
    var parents = flowers.filter(function (f) { return f && f.role === "parent"; }).sort(bySlotIndex);
    var children = flowers.filter(function (f) { return f && f.role === "child"; }).sort(bySlotIndex);
    if (!parents.length && !children.length && flowers.length) children = flowers;

    function bySlotIndex(a, b) { return (a.slotIndex | 0) - (b.slotIndex | 0); }

    // The mode follows the number of PEOPLE (the captions), not the paintings drawn: six
    // people with two februari draw five images and are still a 6+ poster.
    var peopleCount = (input && input.captions && input.captions.length) ? input.captions.length : flowers.length;
    var layoutScale = (input && typeof input.globalScale === "number" && input.globalScale > 0) ? input.globalScale : 1;
    var stemEnds = null;
    if (imagesByKey) {
      // Where each painted stem ENDS, measured once per image, so every flower can be turned
      // toward the aim without anybody rotating it by hand.
      stemEnds = {};
      flowers.forEach(function (f) {
        var se = f && imagesByKey[f.key] && measureStemEnd(imagesByKey[f.key], getFlowerAnchor(f.flowerKey || f.key) || getFlowerOverride(f.key).stemAnchor);
        if (se) stemEnds[canonKey(f.flowerKey || f.key)] = se;
      });
    }
    var vaseFloor = vaseFloorFor(input && input.vase, imagesByKey, layoutArtboard);
    var bouquet = computeBouquetSlots(layoutArtboard, parents, children, {
      peopleCount: peopleCount, globalScale: layoutScale, stemEnds: stemEnds, vase: input && input.vase,
      vaseFloor: vaseFloor
    });
    // Draw order = painter's algorithm by resolved y: the further DOWN a flower sits,
    // the more in front it is (matches real tied bouquets). Template z only breaks
    // ties between flowers at the exact same height.
    // Draw order: manual zBias layer FIRST (dev-forced forward/back), then painter's by
    // resolved y (lower = more in front), then template z as the final tiebreak.
    // y still sets the NATURAL order -- the further down a flower sits, the more in front
    // it is, the way a real arrangement stacks. dz is a step against that order, not a
    // layer above it: sorting on zBias first meant any non-zero dz leapt a flower clear of
    // every flower without one, so there was no way to put june just in front of march.
    //
    // Rank by y first, then shift a flower by dz places within that ranking. +1 moves it
    // one flower forward, -1 one back, and 0 leaves it wherever the arrangement puts it.
    var slotsByZ = orderSlotsForDrawing(bouquet.slots);

    var globalScale = (input && typeof input.globalScale === "number" && input.globalScale > 0) ? input.globalScale : 1;

    // Two passes: stems first (all behind every head, like a real tied bouquet), then
    // heads. A flower with a separate stem asset (slot.stemKey) gets both drawn with the
    // IDENTICAL transform -- the stem PNG must share the head PNG's canvas size so they
    // register perfectly. Flowers without a stem asset are one-piece images (head pass only).
    function anchorFor(slot) {
      // Dev-set species center wins; block-settings anchor is the fallback.
      return getFlowerAnchor(slot.flowerKey) || getFlowerOverride(slot.key).stemAnchor;
    }

    // The whole flower layer (stems + heads) is CLIPPED to the VISIBLE RECT (artboard) so
    // NOTHING (stray stem tips, leaf edges) ever shows outside the colored panel. The
    // bouquet is still POSITIONED in the frozen layoutArtboard frame (so it never moves),
    // but the frozen frame is ~27px taller / a bit wider than the reference rect and sits
    // fully inside it, so clipping to the rect just trims the small overhang.
    ctx.save();
    ctx.beginPath();
    ctx.rect(artboard.x, artboard.y, artboard.w, artboard.h);
    ctx.clip();

    // Stems first (all behind every head, like a real tied bouquet): each stem GROWS
    // from its flower's center down to the tie point, stretched/rotated to fit. The
    // bottom ends get a small deterministic scatter (a few px per position) so the
    // stems bundle instead of converging into one exact point. Flowers without a stem
    // asset are one-piece images and skip this pass entirely.
    // With a vase drawn, the flowers (and their shadows) are also clipped to everything above the
    // vase floor: a stem longer than the vessel disappears inside the vase instead of showing
    // under it. Heads never move to make a stem fit (computeCellSlots), so this is what hides it.
    // The line is the stem cut (getStemCut / effectiveStemCut): tuned, or by default the floor or the
    // painted vessel's bottom when that is higher, so nothing shows under or beside the vase base.
    // input.stemCutoffPx (the bouquet's +- px band at the tie point) is NOT this and is not read.
    var stemCut = effectiveStemCut(input && input.vase, imagesByKey, layoutArtboard);
    var floorClip = finite(stemCut);
    if (floorClip) {
      var clipY = layoutArtboard.y + stemCut * layoutArtboard.h;
      ctx.save();
      ctx.beginPath();
      ctx.rect(artboard.x, artboard.y, artboard.w, clipY - artboard.y);
      // Below the line only the part under the vase is cut: beside it (petals lying on the table)
      // everything is drawn. The vase is placed on the visible artboard, like its drawing below.
      var cutBand = stemCutBand(input && input.vase, imagesByKey, artboard);
      var belowH = artboard.y + artboard.h - clipY;
      if (cutBand && belowH > 0) {
        var bl = artboard.x + cutBand.l * artboard.w, br = artboard.x + cutBand.r * artboard.w;
        if (bl > artboard.x) ctx.rect(artboard.x, clipY, bl - artboard.x, belowH);
        if (br < artboard.x + artboard.w) ctx.rect(br, clipY, artboard.x + artboard.w - br, belowH);
      }
      ctx.clip();
    }
    slotsByZ.forEach(function (slot) {
      var stemImg = slot.stemKey && imagesByKey && imagesByKey[slot.stemKey];
      if (!stemImg) return;
      var headImg = imagesByKey && imagesByKey[slot.key];
      var cf = parseCmFactor(slot.cmFactor);
      var box = FLOWER_BASE_SIZE * (slot.scale || 1) * globalScale;
      var headScale = headImg
        ? Math.min(box / (headImg.naturalWidth || box), box / (headImg.naturalHeight || box)) * cf
        : (box / 500) * cf;
      var jx = ((slot.tplIndex * 53) % 25) - 12; // deterministic scatter, Â±12px / Â±5px
      var jy = ((slot.tplIndex * 29) % 11) - 5;
      var flip = !!slot.flip;
      drawStemStretched(ctx, stemImg, slot.x, slot.y, bouquet.tiePoint.x + jx, bouquet.tiePoint.y + jy, headScale, flip);
    });
    slotsByZ.forEach(function (slot) {
      var img = imagesByKey && imagesByKey[slot.key];
      if (!img) return;
      var flip = !!slot.flip;
      drawFlowerAt(ctx, img, slot, style, slot.cmFactor, anchorFor(slot), flip, globalScale);
    });
    if (floorClip) ctx.restore();   // end floor clip (the artboard clip stays for the vase)

    // ---- Vase: drawn OVER every flower, inside the same artboard clip. These flowers are
    // one-piece paintings with their stems included, so there is no separate stem layer to
    // slide a vessel between -- the vase's own silhouette is what hides the stems. That is
    // why this replaced the stem-cutoff band: it trims in the shape of the actual vase
    // instead of a coloured rectangle across the poster.
    // Placement comes from the section (one shared placement for every vase): the image's
    // TOP-CENTRE lands on (mouthX, mouthY), width is a fraction of the artboard and the
    // height follows each image's own proportions, so nothing is ever squashed.
    var vase = input && input.vase;
    var vaseImg = vase && vase.key && imagesByKey && imagesByKey[vase.key];
    if (vaseImg && (vaseImg.naturalWidth || vaseImg.width)) {
      var viw = vaseImg.naturalWidth || vaseImg.width;
      var vih = vaseImg.naturalHeight || vaseImg.height || viw;
      var vwFrac = Number(vase.width); if (!isFinite(vwFrac) || vwFrac <= 0) vwFrac = 0.3;
      var vmx = Number(vase.mouthX); if (!isFinite(vmx)) vmx = 0.5;
      var vmy = Number(vase.mouthY); if (!isFinite(vmy)) vmy = 0.62;
      vmx = Math.min(1, Math.max(0, vmx));
      vmy = Math.min(1, Math.max(0, vmy));
      var vw = artboard.w * vwFrac;
      var vh = vw * (vih / viw);
      // WHERE the mouth sits inside the artwork. A vase painted with its cast shadow
      // spreading to one side makes the file much wider than the vessel, so the middle of
      // the image is NOT the middle of the vase -- anchoring on the file would shove the
      // vessel off to one side. Defaults (0.5, 0) reproduce top-centre anchoring exactly.
      var iax = Number(vase.imgMouthX); if (!isFinite(iax)) iax = 0.5;
      var iay = Number(vase.imgMouthY); if (!isFinite(iay)) iay = 0;
      iax = Math.min(1, Math.max(0, iax));
      iay = Math.min(1, Math.max(0, iay));
      ctx.drawImage(vaseImg,
        artboard.x + artboard.w * vmx - vw * iax,
        artboard.y + artboard.h * vmy - vh * iay,
        vw, vh);
    }

    ctx.restore(); // end artboard clip


    // ---- TEXT: title ABOVE the art, paired captions below it.
    // This is where the vase stops being a bouquet. The bouquet stacks a headline, then
    // every name on one line, then every flower on another. The vase splits them: the
    // title sits above the artwork, and each person becomes a NAME-over-FLOWER pair in a
    // fixed 4-column grid underneath, with hairline rules on the COLUMN BOUNDARIES.
    // Rows fill left to right and a short final row is centred, which is what makes a
    // six-person poster read 4 + 2 with the short row tucked under the middle columns.
    var lsEm = (typeof style.letterSpacingEm === "number") ? style.letterSpacingEm : 0.5;
    var centerX = DESIGN_W / 2;
    var padX = (style.footerPaddingXPct || 0.078) * DESIGN_W;

    ctx.fillStyle = style.textColor || "#111111";
    ctx.textAlign = "left";        // drawSpacedText centres manually from centerX
    ctx.textBaseline = "middle";

    // Shrink text until it fits: every string here is merchant/customer typed, so any of
    // them can be longer than the space allows.
    function fitPx(text, startPx, maxW) {
      var px = startPx;
      ctx.font = "400 " + px + "px " + style.fontFamily + ", RockwellPoster";
      while (px > 4 && measureSpacedWidth(ctx, text, lsEm * px) > maxW) {
        px -= 0.5;
        ctx.font = "400 " + px + "px " + style.fontFamily + ", RockwellPoster";
      }
      return px;
    }

    // --- Title + ingress, in the margin above the artboard ---
    var headline = String((input && input.headline) || "").trim().toUpperCase();
    var ingress = String((input && input.ingress) || "").trim().toUpperCase();
    var maxTitleW = DESIGN_W - 2 * padX;

    if (headline) {
      var hPx = fitPx(headline, (style.titleSizePct || 0.0315) * DESIGN_W, maxTitleW);
      drawSpacedText(ctx, headline, centerX, (style.titleYPct || 0.082) * designH, lsEm * hPx);
    }
    if (ingress) {
      var iPx = fitPx(ingress, (style.ingressSizePct || 0.0145) * DESIGN_W, maxTitleW);
      drawSpacedText(ctx, ingress, centerX, (style.ingressYPct || 0.116) * designH, lsEm * iPx);
    }

    // --- Caption grid: one NAME / FLOWER pair per person ---
    // The caption grid lists PEOPLE. Under the "pair" rule two people share one painted
    // image, so counting what was drawn would quietly lose a name.
    var captionSource = (input && input.captions && input.captions.length) ? input.captions : flowers;
    var pairs = [];
    for (var j = 0; j < captionSource.length; j++) {
      var e = captionSource[j];
      if (!e || typeof e !== "object") continue;
      var nm = String(e.name || "").trim().toUpperCase();
      // The poster flower name ("ROS"), not the picker's card label (often the month).
      var fl = String(e.posterName || "").trim().toUpperCase();
      if (nm || fl) pairs.push({ name: nm, flower: fl });
    }

    if (pairs.length) {
      // NAMES on their own lines, FLOWERS below them:
      //
      //   AUGUST, MATILDA, VILGOT, SIRI, EMMA & ELLA
      //   DAISY / DAISY / LARKSPUR / MARIGOLD / ROSE / VIOLET
      //
      // Same row rule as the paired grid it replaces: up to 6 per row on one row, 7-12
      // split into two balanced rows, each centred. The "&" joins the last two names of
      // the WHOLE list, not of each row -- it closes the sentence, so it belongs at the
      // end of the last row and nowhere else.
      var perRow = (style.captionMaxPerRow || 6);

      function splitRows(items) {
        if (items.length <= perRow) return [items.slice()];
        var firstN = Math.ceil(items.length / 2);
        if (firstN > perRow) firstN = perRow;
        return [items.slice(0, firstN), items.slice(firstN)];
      }

      var names = pairs.map(function (p) { return p.name; }).filter(Boolean);
      var flowerNames = pairs.map(function (p) { return p.flower; }).filter(Boolean);

      // Rebuild each row's text with the separator that FOLLOWS each item, so the "&"
      // lands before the final name wherever the split happens to fall.
      function nameRowText(rows, r) {
        var row = rows[r], isLast = (r === rows.length - 1), out = "";
        row.forEach(function (n, i) {
          var lastOverall = isLast && i === row.length - 1;
          var secondLastOverall = isLast && i === row.length - 2;
          out += n;
          if (!lastOverall) out += secondLastOverall ? " & " : ", ";
        });
        return out;
      }

      var nameRows = splitRows(names);
      var flowerRows = splitRows(flowerNames);

      var maxW = DESIGN_W - 2 * padX;
      var namePx = (style.captionNameSizePct || 0.0135) * DESIGN_W;
      var flowerPx = namePx * (style.captionFlowerRatio || 0.72);

      // One size per block, fitted to the longest row: the lines have to read as one
      // list, and a row that shrank on its own would look like a different list.
      var nameTexts = nameRows.map(function (_, r) { return nameRowText(nameRows, r); });
      nameTexts.forEach(function (t) { namePx = Math.min(namePx, fitPx(t, namePx, maxW)); });

      var flowerTexts = flowerRows.map(function (row) { return row.join(" / "); });
      flowerTexts.forEach(function (t) { flowerPx = Math.min(flowerPx, fitPx(t, flowerPx, maxW)); });

      var lineGap = namePx * (style.captionLineGapRatio || 1.6);       // row -> row
      var blockGap = namePx * (style.captionBlockGapRatio || 2.0);     // names -> flowers
      var y = footer.y + (style.captionTopGapPct || 0.015) * designH + namePx / 2;

      nameTexts.forEach(function (t, r) {
        ctx.font = "400 " + namePx + "px " + style.fontFamily + ", RockwellPoster";
        drawSpacedText(ctx, t, centerX, y + r * lineGap, lsEm * namePx);
      });

      y += (nameTexts.length - 1) * lineGap + blockGap;

      flowerTexts.forEach(function (t, r) {
        ctx.font = "400 " + flowerPx + "px " + style.fontFamily + ", RockwellPoster";
        drawSpacedText(ctx, t, centerX, y + r * (flowerPx * (style.captionLineGapRatio || 1.6)), lsEm * flowerPx);
      });
    }

    // Returned so callers (e.g. dev-mode tooling) can hit-test clicks against the
    // actual resolved positions without recomputing the layout themselves.
    bouquet.panel = { x: layoutArtboard.x, y: layoutArtboard.y, w: layoutArtboard.w, h: layoutArtboard.h };
    bouquet.stemCut = finite(stemCut) ? stemCut : null;
    bouquet.stemCutBand = finite(stemCut) ? stemCutBand(input && input.vase, imagesByKey, artboard) : null;
    return bouquet;
  }

  // ============================================================
  // 9) EXPORTS
  // ============================================================
  function safeFilename(s) {
    return String(s || "").trim().replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-_]/g, "");
  }

  function downloadDataUrl(dataUrl, filename) {
    var a = document.createElement("a");
    a.download = filename;
    a.href = dataUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function dataUrlToBlob(dataUrl) {
    var parts = dataUrl.split(",");
    var mime = parts[0].match(/:(.*?);/)[1];
    var bin = atob(parts[1]);
    var arr = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  // ---- sRGB tagging for PNG exports -------------------------------------------------
  // A <canvas> PNG holds sRGB pixels but carries NO color-profile chunk, so some print
  // pipelines guess the space (or convert to CMYK) and shift the colors. We inject the
  // standard sRGB + gAMA + cHRM chunks right after IHDR so the file is unambiguously
  // tagged sRGB. Pixels are untouched -- this is metadata only.
  var _CRC_TABLE = (function () {
    var t = new Uint32Array(256);
    for (var n = 0; n < 256; n++) { var c = n; for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1); t[n] = c >>> 0; }
    return t;
  })();
  function _crc32(bytes) {
    var c = 0xFFFFFFFF;
    for (var i = 0; i < bytes.length; i++) c = _CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
  }
  function _u32(v) { return new Uint8Array([(v >>> 24) & 255, (v >>> 16) & 255, (v >>> 8) & 255, v & 255]); }
  function _pngChunk(type, data) {
    var len = data.length, out = new Uint8Array(12 + len);
    out.set(_u32(len), 0);
    for (var i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(data, 8);
    out.set(_u32(_crc32(out.subarray(4, 8 + len))), 8 + len);
    return out;
  }
  function tagPngSrgb(bytes) {
    var ihdrEnd = 8 + 12 + 13; // signature + IHDR chunk
    if (bytes.length < ihdrEnd || bytes[0] !== 0x89 || bytes[1] !== 0x50) return bytes; // not a PNG
    var sRGB = _pngChunk("sRGB", new Uint8Array([0])); // 0 = perceptual rendering intent
    var gAMA = _pngChunk("gAMA", _u32(45455));         // 1/2.2, sRGB spec
    var chrm = [31270, 32900, 64000, 33000, 30000, 60000, 15000, 6000]; // sRGB primaries x1e5
    var chrmData = new Uint8Array(32);
    for (var i = 0; i < 8; i++) chrmData.set(_u32(chrm[i]), i * 4);
    var cHRM = _pngChunk("cHRM", chrmData);
    var head = bytes.subarray(0, ihdrEnd), tail = bytes.subarray(ihdrEnd);
    var out = new Uint8Array(head.length + sRGB.length + gAMA.length + cHRM.length + tail.length), o = 0;
    out.set(head, o); o += head.length;
    out.set(sRGB, o); o += sRGB.length;
    out.set(gAMA, o); o += gAMA.length;
    out.set(cHRM, o); o += cHRM.length;
    out.set(tail, o);
    return out;
  }
  function canvasToSrgbPngDataUrl(canvas) {
    var url = canvas.toDataURL("image/png");
    try {
      var b64 = url.slice(url.indexOf(",") + 1), bin = atob(b64);
      var bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      var tagged = tagPngSrgb(bytes), s = "";
      for (var j = 0; j < tagged.length; j++) s += String.fromCharCode(tagged[j]);
      return "data:image/png;base64," + btoa(s);
    } catch (e) { return url; } // any failure -> plain (still sRGB) PNG
  }

  function calcPrintLongSide(sizeKey, dpi) {
    dpi = dpi || 300;
    var mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    var longMm = Math.max(mm.w, mm.h);
    return Math.round((longMm / 25.4) * dpi);
  }

  function dimsForLongSide(sizeKey, longSide) {
    var mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    var ratio = mm.h / mm.w;
    if (ratio >= 1) return { w: Math.round(longSide / ratio), h: longSide };
    return { w: longSide, h: Math.round(longSide * ratio) };
  }

  // ---- sRGB ICC profile (IEC61966-2.1, 3144 bytes, lifted from a Photoshop-exported PDF).
  // Embedded as a PDF OutputIntent so Photoshop/print RIPs read the PDF as sRGB instead of
  // assuming another RGB working space (which made our exports look bluish vs the source).
  var SRGB_ICC_B64 = "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";
  var _srgbIccBytes = null;
  function srgbIccBytes() {
    if (_srgbIccBytes) return _srgbIccBytes;
    try { var bin = atob(SRGB_ICC_B64), a = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i); _srgbIccBytes = a;
    } catch (e) { _srgbIccBytes = null; }
    return _srgbIccBytes;
  }
  function addSrgbOutputIntent(doc) {
    try {
      var icc = srgbIccBytes(); if (!icc || !doc || !doc.internal || !doc.internal.events) return;
      var internal = doc.internal, iccObjNum = null;
      internal.events.subscribe("postPutResources", function () {
        iccObjNum = internal.newObject();
        doc.__srgbIccObjNum = iccObjNum; // captured so the image can be retagged ICCBased sRGB
        internal.out("<</N 3/Alternate/DeviceRGB/Length " + icc.length + ">>");
        internal.out("stream");
        var s = ""; for (var i = 0; i < icc.length; i++) s += String.fromCharCode(icc[i]);
        internal.out(s);
        internal.out("endstream");
        internal.out("endobj");
      });
      internal.events.subscribe("putCatalog", function () {
        if (iccObjNum == null) return;
        internal.out("/OutputIntents[<</Type/OutputIntent/S/GTS_PDFA1/OutputConditionIdentifier(sRGB IEC61966-2.1)/Info(sRGB IEC61966-2.1)/DestOutputProfile " + iccObjNum + " 0 R>>]");
      });
    } catch (e) { /* never block export on color-tagging */ }
  }

  // Retag the (single, DeviceRGB) JPEG image as ICCBased sRGB, reusing the ICC stream
  // object that addSrgbOutputIntent already embedded. Photoshop reads the IMAGE's
  // colorspace when opening a PDF and ignores the doc-level OutputIntent, so a bare
  // /DeviceRGB image gets reinterpreted in the viewer's RGB working space -> bluish shift.
  // We do a single byte insertion (/DeviceRGB -> [/ICCBased N 0 R]) and repair the xref by
  // shifting only the offsets that sit AFTER the insertion by a fixed delta -- no stream
  // rescanning (which would risk matching object-like bytes inside the JPEG/ICC streams).
  function _bytesToLatin1(u8) {
    var s = "", CH = 8192;
    for (var i = 0; i < u8.length; i += CH) {
      s += String.fromCharCode.apply(null, u8.subarray(i, Math.min(i + CH, u8.length)));
    }
    return s;
  }
  function _latin1ToBytes(s) {
    var out = new Uint8Array(s.length);
    for (var i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
    return out;
  }
  function retagPdfImageSrgb(arrayBuffer, iccObjNum) {
    var u8 = new Uint8Array(arrayBuffer);
    if (iccObjNum == null) return u8;
    try {
      var s = _bytesToLatin1(u8);
      var target = "/ColorSpace /DeviceRGB";
      var idx = s.indexOf(target);
      if (idx < 0) return u8; // image already tagged / not found -> leave untouched
      var repl = "/ColorSpace [/ICCBased " + iccObjNum + " 0 R]";
      var delta = repl.length - target.length;

      var sxPos = s.lastIndexOf("startxref");
      var mSx = s.slice(sxPos).match(/startxref\s+(\d+)/);
      if (sxPos < 0 || !mSx) return u8; // unexpected structure -> bail, keep original
      var xrefOff = parseInt(mSx[1], 10);
      if (!(xrefOff > idx)) return u8; // xref must follow the insertion for the shift to hold

      // Body up to the xref table, with the replacement applied.
      var body = s.slice(0, idx) + repl + s.slice(idx + target.length, xrefOff);
      var newXrefOff = xrefOff + delta;

      // xref table + trailer (everything from `xref` up to `startxref`), offsets shifted.
      var xrefTail = s.slice(xrefOff, sxPos).replace(
        /(\d{10}) (\d{5}) n/g,
        function (mm, off, gen) {
          var ov = parseInt(off, 10);
          if (ov > idx) ov += delta;
          return ("0000000000" + ov).slice(-10) + " " + gen + " n";
        }
      );

      var outStr = body + xrefTail + "startxref\n" + newXrefOff + "\n%%EOF\n";
      return _latin1ToBytes(outStr);
    } catch (e) {
      return u8; // never break the download over color-tagging
    }
  }

  async function exportJpg(opts) {
    var input = opts.input;
    var imagesByKey = opts.imagesByKey;
    var style = opts.style;
    var longSide = opts.longSide || 1200;
    var quality = opts.quality || 0.9;
    var sizeKey = opts.sizeKey || (input && input.sizeKey) || "50x70";

    var dims = dimsForLongSide(sizeKey, longSide);
    var c = makeOffscreenCanvas(dims.w, dims.h);
    renderPoster(c.offCtx, imagesByKey, Object.assign({}, input, { sizeKey: sizeKey }), style);
    return c.off.toDataURL("image/jpeg", quality);
  }

  async function exportPng(opts) {
    var input = opts.input;
    var imagesByKey = opts.imagesByKey;
    var style = opts.style;
    var longSide = opts.longSide || 1200;
    var sizeKey = opts.sizeKey || (input && input.sizeKey) || "50x70";

    var dims = dimsForLongSide(sizeKey, longSide);
    var c = makeOffscreenCanvas(dims.w, dims.h);
    renderPoster(c.offCtx, imagesByKey, Object.assign({}, input, { sizeKey: sizeKey }), style);
    return canvasToSrgbPngDataUrl(c.off); // sRGB-tagged PNG (correct print color profile)
  }

  async function exportPdf(opts) {
    var input = opts.input;
    var imagesByKey = opts.imagesByKey;
    var style = opts.style;
    var size = opts.size;
    var dpi = opts.dpi || 300;
    var jpegQuality = opts.jpegQuality || 0.95;
    var bleedMm = (typeof opts.bleedMm === "number" && opts.bleedMm > 0) ? opts.bleedMm : 0;

    if (!window.jspdf || !window.jspdf.jsPDF) throw new Error("jsPDF not loaded.");
    var mm = SIZE_MM[size];
    if (!mm) throw new Error("Unknown size for PDF: " + size);
    var pxW = mmToPx(mm.w, dpi);
    var pxH = mmToPx(mm.h, dpi);
    // The bleed is RENDERED, not reconstructed: the canvas is the full sheet and
    // the poster is drawn into it offset by the bleed, so the paper colour and
    // the paper texture continue right off the edge. The two things we did instead
    // both looked wrong on the sheet -- a flat rect in the sampled corner colour
    // (untextured band, seam at the trim line), and the outermost pixel row
    // stretched across 4 mm (the texture smeared into streaks).
    var bleedPx = bleedMm > 0 ? Math.round(bleedMm * (pxW / mm.w)) : 0;
    var c = makeOffscreenCanvas(pxW, pxH, bleedPx);
    renderPoster(c.offCtx, imagesByKey, Object.assign({}, input || {}, {
      sizeKey: size, bleedDesignPx: c.bleedDesignPx
    }), style);

    // PDF/X-4 verification variant: hand the finished sheet to pdfx4.js (sRGB-tagged
    // artwork + embedded output intent, bleed already rendered) instead of the jsPDF path
    // below. Additive only -- exercised solely by the admin "PDF/X-4" verification
    // button, which passes variant:'pdfx4'. Every storefront and legacy caller omits
    // it and falls straight through to the unchanged jsPDF export.
    if (opts.variant === "pdfx4") {
      if (typeof window === "undefined" || !window.PDFX4) {
        throw new Error("PDF/X-4 writer (pdfx4.js) not loaded -- reload the page and retry.");
      }
      return window.PDFX4.exportCanvas(c.off, {
        trimWidthMm: mm.w,
        trimHeightMm: mm.h,
        bleedMm: bleedMm,
        sourceIncludesBleed: bleedPx > 0, // c.off is already the full sheet
        jpegQuality: jpegQuality,
        releaseSource: true,
        title: opts.title || "Poster",
        creator: "Tildas poster admin"
      });
    }

    var imgData = c.off.toDataURL("image/jpeg", jpegQuality);
    var jsPDF = window.jspdf.jsPDF;
    // Page = trim size + `bleedMm` on every side, and the raster IS that page --
    // one image edge to edge, no fill rect, because the bleed was rendered above.
    // With bleedMm 0 the page is the trim size and nothing extra is emitted.
    var pageW = mm.w + bleedMm * 2;
    var pageH = mm.h + bleedMm * 2;
    var pdf = new jsPDF({
      orientation: pageH >= pageW ? "portrait" : "landscape",
      unit: "mm",
      format: [pageW, pageH],
    });
    addSrgbOutputIntent(pdf); // embed sRGB IEC61966-2.1 so the PDF isn't read as a wrong RGB space
    pdf.addImage(imgData, "JPEG", 0, 0, pageW, pageH);
    // Generate bytes, then retag the image as ICCBased sRGB (see retagPdfImageSrgb).
    var ab = pdf.output("arraybuffer");
    var bytes = retagPdfImageSrgb(ab, pdf.__srgbIccObjNum);
    return new Blob([bytes], { type: "application/pdf" });
  }

  // ============================================================
  // 10) PUBLIC API
  // ============================================================
  if (typeof console !== "undefined") console.log("[FVR] flower-vase-renderer " + VERSION);

  return {
    VERSION: VERSION,
    DESIGN_W: DESIGN_W,
    DEFAULT_GLOBAL_SCALE: DEFAULT_GLOBAL_SCALE,
    SIZE_MM: SIZE_MM,
    DEFAULT_STYLE: DEFAULT_STYLE,
    designHeightFor: designHeightFor,

    setupPreviewCanvas: setupPreviewCanvas,
    setupPreviewCanvasForSize: setupPreviewCanvasForSize,
    ensureFontLoaded: ensureFontLoaded,
    loadImageTo: loadImageTo,

    upsertFlower: upsertFlower,
    upsertFlowerOverride: upsertFlowerOverride,
    setDefaultFlowerKey: setDefaultFlowerKey,
    BG_KEY: BG_KEY,
    TEXTURE_KEY: TEXTURE_KEY,
    computeBouquetSlots: computeBouquetSlots,
    orderSlotsForDrawing: orderSlotsForDrawing,

    // 6+ people: size classes, cells, nudges, aim (contract: vase-cells-api)
    getVaseClass: getVaseClass,
    setVaseClass: setVaseClass,
    cellKeyFor: cellKeyFor,
    getClassRow: getClassRow,
    setClassRow: setClassRow,
    getCellSlots: getCellSlots,
    hasCountSlots: hasCountSlots,
    setCellSlot: setCellSlot,
    copyCell: copyCell,
    restoreCell: restoreCell,
    getCellNudge: getCellNudge,
    setCellNudge: setCellNudge,
    getCellLSlot: getCellLSlot,
    getCellLBase: getCellLBase,
    setCellLSlot: setCellLSlot,
    getCellLNudge: getCellLNudge,
    setCellLNudge: setCellLNudge,
    getAim: getAim,
    setAim: setAim,
    getStemCut: getStemCut,
    setStemCut: setStemCut,
    effectiveStemCut: effectiveStemCut,
    setStemEnd: setStemEnd,
    ringSlots: ringSlots,
    cellPairs: cellPairs,
    getTunedCellCounts: getTunedCellCounts,
    getBakedVaseClasses: getBakedVaseClasses,
    BOUQUET_CX: BOUQUET_CX,
    MEDIUM_MIN_GAP: MEDIUM_MIN_GAP,
    stemTipFromAlpha: stemTipFromAlpha,
    measureStemEnd: measureStemEnd,
    VASE_INNER_HALF: VASE_INNER_HALF,
    vaseFloorFor: vaseFloorFor,

    // Dev-mode template tuning (see sections/flower-poster.liquid's "Dev mode" panel)
    setTemplateOverrides: setTemplateOverrides,
    nudgeSlot: nudgeSlot,
    nudgeSlotScale: nudgeSlotScale,
    nudgeSlotRot: nudgeSlotRot,
    nudgeFlowerOffset: nudgeFlowerOffset,
    getFlowerOffset: getFlowerOffset,
    clearFlowerOffset: clearFlowerOffset,
    setFlowerAnchor: setFlowerAnchor,
    setFlowerRotation: setFlowerRotation,
    getFlowerRotation: getFlowerRotation,
    setFlowerFlip: setFlowerFlip,
    setFlowerDirection: setFlowerDirection,
    setSlotFacing: setSlotFacing,
    setOffsetFlip: setOffsetFlip,
    setOffsetFacing: setOffsetFacing,
    getSlotFacing: getSlotFacing,
    getFlowerDirection: getFlowerDirection,
    getFlowerFlip: getFlowerFlip,
    setSpeciesSpot: setSpeciesSpot,
    getSpeciesSpot: getSpeciesSpot,
    comboKeyFor: comboKeyFor,
    toggleFlowerFlip: toggleFlowerFlip,
    resolveRepeats: resolveRepeats,
    baseKeyForSlot: baseKeyForSlot,
    getFlowerAnchor: getFlowerAnchor,
    setFlowerScale: setFlowerScale,
    setMargins: setMargins,
    getMargins: getMargins,
    getFlowerScale: getFlowerScale,
    getTemplateJson: getTemplateJson,
    getAdjustmentsJson: getAdjustmentsJson,
    resetTemplate: resetTemplate,
    resetAllAdjustments: resetAllAdjustments,
    applyBakedStyleOverrides: applyBakedStyleOverrides,
    copySplitTo: copySplitTo,
    copySlotTo: copySlotTo,
    copyFlowerOffsetTo: copyFlowerOffsetTo,
    copyFlowerOffsetsFrom: copyFlowerOffsetsFrom,
    splitKeyFor: splitKeyFor,

    renderPoster: renderPoster,
    exportJpg: exportJpg,
    exportPng: exportPng,
    exportPdf: exportPdf,

    downloadDataUrl: downloadDataUrl,
    safeFilename: safeFilename,
    dataUrlToBlob: dataUrlToBlob,
    calcPrintLongSide: calcPrintLongSide,
  };
});
